/**
 * Tiny fetch wrapper for the local API. Vite proxies /api → :4173 in dev,
 * and the same paths work in prod when the Express server serves the
 * built bundle from web/dist/.
 */
import { emitAuthExpired } from "./auth";
// Type-only import from the pipeline's Remotion tree: the Studio player
// mounts the REAL ClipComposition, so its inputProps ARE the render's
// ClipProps — one shape, no drift. Erased at build time.
import type { ClipProps } from "../../src/remotion/types.ts";
// Same-shape sharing for the Studio chat loop: the iterate endpoint
// returns the pipeline's own EditPatch/ClipEditState types. Erased at
// build time.
import type { ClipEditState, EditPatch } from "../../src/lib/clipEditState.ts";

export type JobStatus =
  | "queued"
  | "clipping"
  | "transcribing"
  | "translating"
  | "rendering"
  | "ready"
  | "failed"
  | "incomplete";

export interface JobInput {
  /** YouTube source URL. Optional when `localSource` is present (upload mode). */
  url?: string;
  /** Start timestamp (MM:SS). Optional when `localSource` is present (upload mode). */
  from?: string;
  to?: string;
  duration?: number;
  host?: string;
  guests?: string[];
  topic?: string;
  /** Exact words the clip should begin with — anchors the start to the phrase. */
  startPhrase?: string;
  /** Exact words the clip should end on — anchors the end to the last matching word. */
  endPhrase?: string;
  /** Opt-in pan-detection rescue: denser sampling + outlier rejection. Set by
   *  the "Retry · Fine pan" UI action. */
  finePan?: boolean;
  /** Trust `from` verbatim (clip.ts --no-snap). Set when rendering a Backlog
   *  scene whose start was seconds-nudged in the boundary-adjust flow —
   *  without it the sentence-break snap would pull the nudge back. */
  noSnap?: boolean;
  /** Operator-picked background music filename (e.g. "acoustic-vibes.mp3").
   *  Must match one returned by `listMusicTracks()`. Omitted → Claude pick. */
  musicTrack?: string;
  /** Set when rendering an auto-scan candidate: the host is a scan guess, so
   *  the pipeline runs roster-aware speaker identification to correct it. */
  autoScan?: boolean;
  /** Provenance of `host` on an auto-scan candidate: "operator" (typed in the
   *  form — authoritative, must not be overridden) or "scan" (model guess —
   *  correctable). When "operator", the spawn harness locks the name via an
   *  operator-speaker override so resolveSpeaker won't re-identify it. */
  hostSource?: "operator" | "scan";
  /** Upload mode: the uploadId from POST /api/uploads. Present → skip url/from. */
  localSource?: string;
}

/** A music track with its operator-controlled metadata. `startOffsetSec`
 *  is where playback should begin during a render — the "good part" of
 *  the song the operator pinned. 0 means "use the whole track from the
 *  top" (legacy default). */
export interface MusicTrack {
  filename: string;
  startOffsetSec: number;
  /** ISO-8601 upload time, if recorded. Informational. */
  addedAt?: string;
}

/** Fetch the catalog of background music tracks available to the picker.
 *  Source of truth: whatever lives under R2's `music/` prefix. The
 *  backend filters to known audio extensions and sorts alphabetically
 *  so the same track always lands in the same UI position.
 *
 *  Returns rich track objects (filename + pinned offset). The picker
 *  surfaces the offset inline so the operator sees, at a glance, where
 *  the render will start. Falls back to a synthesised list with
 *  offset=0 if the server only returns the legacy `tracks: string[]`
 *  payload (e.g. an old build still deployed somewhere). */
export async function listMusicTracks(): Promise<MusicTrack[]> {
  const r = await getJSON<{ tracks?: string[]; items?: MusicTrack[] }>("/api/music-tracks");
  if (Array.isArray(r.items) && r.items.length > 0) {
    return r.items.map((it) => ({
      filename: it.filename,
      startOffsetSec: Number.isFinite(it.startOffsetSec) ? Math.max(0, it.startOffsetSec) : 0,
      addedAt: typeof it.addedAt === "string" ? it.addedAt : undefined,
    }));
  }
  // Back-compat fallback — old server build, no items array.
  return (r.tracks ?? []).map((filename) => ({ filename, startOffsetSec: 0 }));
}

/** Pin (or update) the default start offset for a track. The next render
 *  that selects this track will read the new offset from the sidecar
 *  at pickMusic time and bake it into the clip. Already-rendered clips
 *  are unaffected — their music.json snapshotted the offset they
 *  shipped with. */
export async function updateMusicTrackOffset(
  filename: string,
  startOffsetSec: number,
): Promise<MusicTrack> {
  const r = await apiFetch(`/api/music-tracks/${encodeURIComponent(filename)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ startOffsetSec }),
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(text || `update failed (${r.status})`);
  }
  return (await r.json()) as MusicTrack;
}

/** Upload a local audio file (.mp3 / .m4a / .wav) as a new track. The
 *  browser reads the file as a base64 data URL and POSTs it to the
 *  server, which decodes + writes to R2 and seeds an empty sidecar
 *  (offset=0). 25 MiB hard cap on raw bytes — enough for a 10-min
 *  320kbps mp3, tight enough to keep the JSON body manageable. */
export async function uploadMusicTrack(file: File): Promise<MusicTrack> {
  const MAX_BYTES = 25 * 1024 * 1024;
  if (file.size > MAX_BYTES) {
    throw new Error(`File is ${(file.size / 1024 / 1024).toFixed(1)} MiB; max is 25 MiB.`);
  }
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = (): void => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
  const r = await apiFetch("/api/music-tracks", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ filename: file.name, dataUrl }),
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(text || `upload failed (${r.status})`);
  }
  return (await r.json()) as MusicTrack;
}

/** Build the URL an `<audio>` element can stream from to preview a single
 *  track. Same-origin, auth-cookie-gated. The backend streams the file
 *  from R2 (or local storage) through the editor server, so the client
 *  never sees a presigned R2 URL. */
export function musicPreviewUrl(filename: string): string {
  return `/api/music-preview/${encodeURIComponent(filename)}`;
}

export interface JobLogLine {
  ts: number;
  level: "info" | "warn" | "error" | "ok";
  stage?: string;
  msg: string;
}

export interface Job {
  id: string;
  input: JobInput;
  status: JobStatus;
  progress: number;
  currentStage?: string;
  createdAt: number;
  finishedAt?: number;
  errorMsg?: string;
  sourceTitle?: string;
  sourceUrl?: string;
  /** Server-resolved display speaker (operator-speaker override →
   * render-time resolution → operator-typed input → slug guess).
   * Present on GET /api/jobs/:id responses; absent on raw SSE payloads. */
  speaker?: string;
  /** Server display title (rename → source title unless it is an upload
   * filename/UUID → speaker + first caption words → humanised id). Present
   * on GET /api/jobs/:id responses; absent on raw SSE payloads. Never the
   * id, never a filename — show this, not `sourceTitle`. */
  title?: string;
  logs: JobLogLine[];
}

export interface JobListItem {
  id: string;
  status: JobStatus;
  progress: number;
  title: string;
  speaker?: string;
  durationSec?: number;
  createdAt: number;
  hasVideo: boolean;
  hasCaption: boolean;
  hasTranscript: boolean;
  sourceTitle?: string;
  sourceUrl?: string;
  /** Hydrated by the server from work/<id>/input.json when present —
   * lets the detail screen show the original parameters even for
   * persisted jobs whose live state is gone. */
  input?: JobInput;
  /** Present only on archive entries returned by /api/library?archives=true.
   * When set, the item represents a past render (out/archives/<id>/v<idx>/)
   * and the client should hit the versioned video / thumb endpoints. */
  archiveIdx?: number;
  /** A/V sync guard verdict summary — present only when the guard ran for this
   * clip. `desync: true` → the on-screen picture drifted off the audio. */
  avSync?: { desync: boolean; mismatches: number; checked: number };
}

/**
 * Centralised fetch wrapper. Includes credentials so the session cookie
 * goes along for the ride, and broadcasts a 401 to the auth listener
 * so the app shell can bounce back to the login screen.
 */
async function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  const r = await fetch(input, { credentials: "same-origin", ...init });
  if (r.status === 401) emitAuthExpired();
  return r;
}

async function getJSON<T>(path: string): Promise<T> {
  const r = await apiFetch(path);
  if (!r.ok) {
    // Read the server's error body when the response is non-OK — the
    // backend sets `{ error: "..." }` with friendly text for known
    // failure modes (e.g. SchedulerNotConnectedError → 412 with
    // "Scheduler not connected — paste a token in Settings first.").
    // Falling back to the bare status code dropped the helpful
    // message in the UI ("Couldn't load channels: 412" became the
    // visible error, which told the operator nothing actionable —
    // the AIE-2 incident on 2026-05-17).
    let serverMessage = "";
    try {
      const body = (await r.json()) as { error?: string };
      if (body?.error && typeof body.error === "string") serverMessage = body.error;
    } catch { /* response wasn't JSON; fall back to status */ }
    throw new Error(serverMessage || `${r.status} ${r.statusText}`);
  }
  return r.json() as Promise<T>;
}

export async function listJobs(): Promise<JobListItem[]> {
  const r = await getJSON<{ items: JobListItem[] }>("/api/jobs");
  return r.items;
}

export async function listLibrary(opts?: { includeArchives?: boolean }): Promise<JobListItem[]> {
  const path = opts?.includeArchives ? "/api/library?archives=true" : "/api/library";
  const r = await getJSON<{ items: JobListItem[] }>(path);
  return r.items;
}

export async function getJob(id: string): Promise<Job> {
  return getJSON<Job>(`/api/jobs/${encodeURIComponent(id)}`);
}

/** Rename a clip (PATCH /api/jobs/:id/title). The server trims and caps at
 *  120 chars; the returned `title` is what it stored and what the Library
 *  card + the page will show from now on. */
export async function renameJob(id: string, title: string): Promise<{ title: string }> {
  return patchJSON<{ title: string }>(`/api/jobs/${encodeURIComponent(id)}/title`, { title });
}

export async function createJob(input: JobInput): Promise<{ id: string }> {
  const r = await apiFetch("/api/jobs", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
  return r.json() as Promise<{ id: string }>;
}

/**
 * Upload a raw video file to `POST /api/uploads` and resolve with the
 * server-generated `uploadId` (feed it to `createJob({ localSource })`).
 *
 * Uses `XMLHttpRequest` rather than `fetch` because only XHR exposes
 * `upload.onprogress` — `fetch` cannot report request-body upload progress.
 * `xhr.withCredentials = true` mirrors `apiFetch`'s `credentials: "same-origin"`
 * so the session cookie rides along (same-origin only; never sent cross-origin).
 * The body is the raw file; the browser sets `Content-Type` to
 * `application/octet-stream` automatically. A 401 broadcasts the auth-expired
 * event just like `apiFetch`, bouncing the shell back to the login screen.
 */
export function uploadVideo(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<{ uploadId: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.withCredentials = true;
    xhr.open("POST", `/api/uploads?filename=${encodeURIComponent(file.name)}`);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => {
      if (xhr.status === 401) {
        emitAuthExpired();
        reject(new Error("auth required"));
        return;
      }
      let body: { uploadId?: string; error?: string } = {};
      try {
        body = JSON.parse(xhr.responseText) as { uploadId?: string; error?: string };
      } catch {
        /* non-JSON body; fall through to the status-based error below */
      }
      if (xhr.status >= 200 && xhr.status < 300 && body.uploadId) {
        resolve({ uploadId: body.uploadId });
      } else {
        reject(new Error(body.error || `upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error("network error during upload"));
    xhr.send(file);
  });
}

export async function fetchCaption(id: string): Promise<string | null> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/caption`);
  if (!r.ok) return null;
  return r.text();
}

export async function fetchSrt(id: string): Promise<string | null> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/srt`);
  if (!r.ok) return null;
  return r.text();
}

export interface SyncReport {
  generatedAt: number;
  clipName: string;
  totalCues: number;
  parity: {
    englishCues: number | null;
    arabicCues: number;
    countsMatch: boolean | null;
  };
  audioDrift: {
    sampleSize: number;
    meanMs: number;
    medianMs: number;
    p90AbsMs: number;
    maxAbsMs: number;
    stddevMs: number;
  };
  readability: {
    shortCueCount: number;
    fastCueCount: number;
  };
  verdict: "green" | "yellow" | "red";
  recommendation: { action: "shift"; offsetMs: number; reason: string } | null;
}

export async function fetchSyncReport(id: string): Promise<SyncReport | null> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/sync-report`);
  if (!r.ok) return null;
  return r.json() as Promise<SyncReport>;
}

/** Audio/video sync guard report — verifies the cropped PICTURE matches the
 * AUDIO (a different check from SyncReport, which is captions↔audio). */
export interface AvSyncReport {
  checked: number;
  mismatches: number;
  /** True when the picture is judged to have drifted off the audio. */
  desync: boolean;
  pairs: Array<{ t: number; match: boolean; reason: string }>;
}

export async function fetchAvSync(id: string): Promise<AvSyncReport | null> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/av-sync`);
  if (!r.ok) return null;
  return r.json() as Promise<AvSyncReport>;
}

export interface SettingsInfo {
  brand: { name: string; accent: string };
  fonts: string[];
  models: { transcription: string; captions: string };
  captionTemplatesPath: string;
  apiKeys: { anthropic: "configured" | "missing"; openai: "configured" | "missing" };
  /** Server-authoritative limits so the UI never hardcodes them. `maxClipSec`
   *  is the hard clip-duration cap — clips longer than this are trimmed at
   *  render. Optional for backward-compat with an older server. */
  limits?: { maxClipSec: number };
  /** Flag-gated features the UI should show or hide. Optional so an older
   *  server (or a disabled flag) simply reads as "off". */
  features?: { postLengthControl?: boolean; brandStudy?: boolean; samples?: boolean; aiSettings?: boolean; operatorPanel?: boolean };
}

export async function getSettings(): Promise<SettingsInfo> {
  return getJSON<SettingsInfo>("/api/settings");
}

// ---------- sample clips ("try a video") ----------

/** Mirror of `src/lib/samples.ts`'s `SampleSource`. Declared here rather than
 *  type-imported so the web bundle never reaches into a server module that
 *  pulls node:fs through its import graph. */
export type SampleSource =
  | { kind: "clip"; clipName: string }
  | { kind: "youtube"; url: string; from: string; duration?: number };

/** Mirror of `src/lib/samples.ts`'s `SampleClip`. */
export interface SampleClip {
  id: string;
  title: string;
  speaker: { name: string; description?: string };
  durationSec?: number;
  language?: string;
  source: SampleSource;
  /** Poster image. The server fills this in from a previous render's
   *  thumbnail when the catalog doesn't pin one; absent ⇒ the picker draws
   *  its own placeholder. */
  thumbUrl?: string;
}

/** A catalog entry as `GET /api/samples` returns it: the clip plus the
 *  server's answer to "can this render right now?". `reason` is present on
 *  both verdicts — on an unavailable card it is the greyed-out explanation,
 *  on an available one it is an optimistic note ("will fetch from storage"). */
export interface SampleEntry extends SampleClip {
  available: boolean;
  reason?: string;
}

/**
 * The curated sample catalog. A 404 — `QL_SAMPLES=off`, or a server too old
 * to know the route — reads as an EMPTY catalog, never an error: the picker
 * is already gated on `settings.features.samples`, and a disabled flag must
 * show "nothing here", not a red failure on the first screen a new user sees.
 */
export async function listSamples(): Promise<SampleEntry[]> {
  const r = await apiFetch("/api/samples");
  if (r.status === 404) return [];
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
  const body = (await r.json()) as { samples?: SampleEntry[] };
  return body.samples ?? [];
}

/**
 * Render a sample. Same `{ id }` / error contract as `createJob` — the
 * server runs the sample through the very same `createClipJob`, so a 409
 * ("already in progress", or an unavailable source) arrives with the
 * server's own sentence and belongs inline on the card that was clicked.
 *
 * `host` overrides the catalog's speaker name; omitted ⇒ the sample renders
 * under the speaker it was curated with.
 */
export async function trySample(id: string, host?: string): Promise<{ id: string }> {
  const r = await apiFetch(`/api/samples/${encodeURIComponent(id)}/try`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(host?.trim() ? { host: host.trim() } : {}),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
  return r.json() as Promise<{ id: string }>;
}

// ---------- channels (multi-channel onboarding) ----------

export interface ChannelBrand {
  accent: string;
  captionColor: string;
  captionAccent: string;
  speakerNameColor: string;
  watermark?: string;
  logoPath: string;
  logoSmallPath?: string;
  /** Brand surface ("paper") + supporting-text colours, #RRGGBB. Post canvas
   *  only — no video render effect. Absent → the post pipeline's defaults. */
  paper?: string;
  muted?: string;
  /** `speakerName` = the card NAME's family when it differs from the
   *  description's. Absent → the name uses `speaker`. */
  fonts: { caption: string; punch: string; speaker: string; speakerName?: string };
  fontFaces: { family: string; weight: number; src: string }[];
}

export interface ChannelFeatures {
  videos: boolean;
  posts: boolean;
  originals: boolean;
  autoScan: boolean;   // per D-01; server default is ON (absent key → true), explicit false disables
}

export interface CaptionLayout {
  bottomPx: number;
  fontSizePx: number;
  /** Caption style. Absent → "punch" (founding). "uniform" = every line at
   *  fontSizePx, color only from <em> spans, balanced auto-wrap. */
  captionStyle?: "punch" | "uniform";
  twoLine: { topPx: number; bottomPx: number; gapPx?: number; lineHeight?: number } | null;
  box: { color: string; opacity: number; radiusPx: number; padXPx: number; padYPx: number } | null;
  textShadow: string;
  sidePaddingPx: number;
  lineHeight: number;
  /** CSS letter-spacing (e.g. "0.5px"; negative tightens). Absent → "0.5px". */
  letterSpacing?: string;
  /** Vertical placement of the caption block. Absent → "center" (founding). */
  justify?: "center" | "flex-end";
  /** Accent POLICY. Absent or "spans" → colour comes from the cue's <em>
   *  spans (founding). "line-punch" = the LAST line of a multi-line cue
   *  renders fully in captionAccent (one-liners colour their second half). */
  accentMode?: "spans" | "line-punch";
  /** Hard cap on RENDERED lines per cue; overflow rows merge into the last
   *  allowed row. Absent → the founding behaviour (up to 3 per authored line). */
  maxLines?: 1 | 2 | 3;
  /** Styling v2 + motion (mirror src/lib/channels.ts CaptionLayout). */
  wrapMode?: "authored" | "auto";
  stroke?: { color: string; widthPx: number } | null;
  align?: "center" | "start" | "end";
  textTransform?: "none" | "uppercase";
  opacity?: number;
  karaoke?: { mode: "highlight" | "pop"; color?: string } | null;
  animation?: {
    in?: "fade" | "slide-up" | "pop" | "typewriter" | "word-stagger";
    out?: "fade" | "slide-down" | "pop";
    durationMs?: number;
    outDurationMs?: number;
  } | null;
}
/** Timed text overlay (mirror of src/lib/channels.ts TextOverlaySpec). */
export interface TextOverlaySpec {
  /** ≤ 140 chars; "\n" allowed (rendered as pre-wrap). */
  text: string;
  /** Which brand font role the overlay borrows. */
  fontRole: "caption" | "punch" | "speaker";
  /** 20–200 px on the 2160-wide canvas. */
  fontSizePx: number;
  /** #RRGGBB. */
  color: string;
  /** 0–100 % of canvas width/height; the anchor follows `align`. */
  xPct: number;
  yPct: number;
  align: "start" | "center" | "end";
  /** Seconds into the CONTENT, or `{ fromEnd }` = seconds before it ends. */
  start: number | { fromEnd: number };
  /** 0.5–60 s on screen. */
  durationSec: number;
}

export interface ChannelLayout {
  caption: CaptionLayout;
  /** `xPct` = horizontal centre of the logo as % of canvas width. Absent → 50. */
  logo: { widthPct: number; bottomPx: number; opacity: number; xPct?: number };
  /** Timed text overlays (brand CTA). Absent → nothing rendered. */
  overlays?: TextOverlaySpec[];
  speakerArrow: { src: string; widthPct: number; topPx: number; leftPx: number } | null;
  /** Free x/y placement of the speaker name card (% of 2160×3840 canvas,
   *  top-left anchor). Absent → founding coords (130px / 320px).
   *  `durationSec` = seconds on screen (founding 5); `nameFontPx` = name cap
   *  on the generated still (founding 230); `descriptionWords` = max words in
   *  the role line, 0 = no role line. */
  speakerCard?: {
    xPct: number;
    yPct: number;
    scale?: number;
    durationSec?: number;
    nameFontPx?: number;
    descriptionWords?: number;
  };
  /** Footage treatment. `dim` = black darken layer (absent → founding 0.28);
   *  `brightness`/`contrast` light the footage itself (absent → 1, untouched). */
  video?: { dim?: number; brightness?: number; contrast?: number };
}

export interface Channel {
  slug: string;
  displayName: string;
  brand: ChannelBrand;
  layout: ChannelLayout;
  features: ChannelFeatures;
  scheduling: { credentialsFile: string };
  r2Prefix: string;
  voicePromptFragment?: string;
  /** Optional fixed caption sign-off appended to every video caption; absent → none. */
  socialCta?: string;
  /** Optional caption pacing for the generic (non-Arabic) path. Absent → the
   *  founding generic literals (11 words / 62 chars / 3.2 s, one accent word). */
  cueShape?: { maxWords?: number; maxChars?: number; maxDurSec?: number; accentWords?: 0 | 1 | 2 };
  /** Target caption language. Absent → Arabic (the founding pipeline). */
  language?: {
    code: string;
    displayName: string;
    direction: "rtl" | "ltr";
    register: string;
    budgets: { topLine: number; bottomLine: number; oneLine: number };
    glyphRatio: number;
  };
  /** The Brand Study this channel's look came from (provenance only). */
  brandStudy?: { id: string; at: string };
  /** Set when the brand is archived (hidden, never deleted). Absent → active. */
  archivedAt?: string;
}

export interface ChannelsResponse {
  activeSlug: string;
  channels: Channel[];
}

export async function listChannelsApi(opts: { archived?: boolean } = {}): Promise<ChannelsResponse> {
  return getJSON<ChannelsResponse>(opts.archived ? "/api/channels?archived=1" : "/api/channels");
}

export interface CreateChannelInput {
  slug: string;
  displayName: string;
  brand: {
    accent: string;
    captionColor?: string;
    captionAccent?: string;
    speakerNameColor?: string;
    watermark?: string;
    /** #RRGGBB post-canvas colours; anything else is ignored server-side. */
    paper?: string;
    muted?: string;
    logoDataUrl: string;
  };
  /** Optional uploaded brand font applied to all text roles. */
  font?: { dataUrl: string; family: string };
  /** Optional per-role font FAMILY assignment (upload / existing / founding
   *  AlHurra-Lalezar-Faseh). Absent role = auto. `speakerName` is the card
   *  NAME's own face (Brand Setup's "Display" role); the server keeps it only
   *  once `FontRoleRequest` carries it, so today it is forward-compatible
   *  metadata rather than a live knob. */
  fontRoles?: { caption?: string; punch?: string; speaker?: string; speakerName?: string };
  layout?: Partial<ChannelLayout>;
  features?: Partial<ChannelFeatures>;
  voicePromptFragment?: string;
  socialCta?: string;
  /** Optional caption pacing for the generic (non-Arabic) path. Absent → the
   *  founding generic literals (11 words / 62 chars / 3.2 s, one accent word). */
  cueShape?: { maxWords?: number; maxChars?: number; maxDurSec?: number; accentWords?: 0 | 1 | 2 };
  r2Prefix?: string;
  /** Optional target-look reference: a screenshot of the desired video/caption
   *  style (data URL) + free-text notes. Saved with the channel. */
  reference?: { dataUrl?: string; notes?: string };
  /** Optional target language (absent → Arabic). */
  language?: {
    code: string;
    displayName: string;
    direction: "rtl" | "ltr";
    register: string;
    budgets: { topLine: number; bottomLine: number; oneLine: number };
    glyphRatio: number;
  };
  /** Optional Brand Study provenance stamp (`id` = study id, `at` = ISO). */
  brandStudy?: { id: string; at: string };
}

/**
 * A create that failed, with the HTTP status attached.
 *
 * The caller has to tell "that name is taken" from "the network blinked", and
 * matching English in an error message is not a contract. `status` is.
 */
export class CreateChannelError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "CreateChannelError";
    this.status = status;
  }
}

export async function createChannelApi(input: CreateChannelInput): Promise<Channel> {
  const r = await apiFetch("/api/channels", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!r.ok) {
    let msg = `${r.status} ${r.statusText}`;
    try { const b = await r.json(); if (b?.error) msg = String(b.error); } catch { /* ignore */ }
    throw new CreateChannelError(msg, r.status);
  }
  return r.json() as Promise<Channel>;
}

/** Fetch a single channel's full saved config (for the onboarding-edit pre-fill). */
export async function getChannelApi(slug: string): Promise<Channel> {
  return getJSON<Channel>(`/api/channels/${encodeURIComponent(slug)}`);
}

/** Fetch a channel's logo as an object URL (auth-safe via apiFetch) so the
 *  onboarding-edit preview can display the real logo. null if unavailable. */
export async function fetchChannelLogoObjectUrl(slug: string): Promise<string | null> {
  try {
    const r = await apiFetch(`/api/channels/${encodeURIComponent(slug)}/logo`);
    if (!r.ok) return null;
    return URL.createObjectURL(await r.blob());
  } catch {
    return null;
  }
}

/** The pickable font families: founding + curated library, then every
 *  family a brand uploaded (`kind: "uploaded"`, `channel` = the brand it
 *  lives on — deduped by family server-side). `weight` is the face's real
 *  registered weight (absent on older servers). */
export interface FontFamilyEntry {
  family: string;
  kind: "founding" | "library" | "uploaded";
  weight?: number;
  channel?: string;
}

export async function listFontFamiliesApi(): Promise<FontFamilyEntry[]> {
  try {
    const r = await apiFetch("/api/fonts");
    if (!r.ok) return [];
    const j = (await r.json()) as { families?: FontFamilyEntry[] };
    return j.families ?? [];
  } catch {
    return [];
  }
}

/** Fetch a registered brand-font file (founding or uploaded) as an object URL,
 *  so the onboarding canvas can @font-face-preview the real face. null if unknown. */
export async function fetchFontObjectUrl(family: string): Promise<string | null> {
  try {
    const r = await apiFetch(`/api/fonts/${encodeURIComponent(family)}`);
    if (!r.ok) return null;
    return URL.createObjectURL(await r.blob());
  } catch {
    return null;
  }
}

/** Full-channel edit — re-open the wizard on an existing channel and Save. Same
 *  shape as create, but the slug is immutable (path) and the logo is optional
 *  (a new upload replaces it; omitting it keeps the current logo + font). */
export interface EditChannelInput extends Omit<CreateChannelInput, "slug" | "brand"> {
  brand: Omit<CreateChannelInput["brand"], "logoDataUrl"> & { logoDataUrl?: string };
}

export async function updateChannelFullApi(slug: string, input: EditChannelInput): Promise<Channel> {
  const r = await apiFetch(`/api/channels/${encodeURIComponent(slug)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!r.ok) {
    let msg = `${r.status} ${r.statusText}`;
    try { const b = await r.json(); if (b?.error) msg = String(b.error); } catch { /* ignore */ }
    throw new Error(msg);
  }
  return r.json() as Promise<Channel>;
}

/**
 * Structured styling read of an onboarding reference frame, returned by
 * `POST /api/channels/analyze-reference`. Mirrors the server-side
 * `StylingProfile` (src/prompts/analyzeReference.ts). All fields optional
 * except `overallConfidence` so a low-signal reference degrades gracefully;
 * the wizard pre-fills whatever is present and leaves the rest at defaults.
 */
export interface StylingProfile {
  caption?: {
    placement?: "top" | "center" | "lower-third" | "bottom";
    style?: "one-line" | "two-line-big-punch";
    bodyColorHex?: string;
    punchColorHex?: string;
    approxFontScale?: "small" | "medium" | "large";
  };
  logo?: {
    placement?:
      | "top-left"
      | "top-center"
      | "top-right"
      | "bottom-left"
      | "bottom-center"
      | "bottom-right"
      | "none";
    approxSize?: "small" | "medium" | "large";
  };
  speakerCard?: {
    present: boolean;
    placement?:
      | "top-left"
      | "top-center"
      | "top-right"
      | "bottom-left"
      | "bottom-center"
      | "bottom-right"
      | "none";
  };
  font?: {
    classification?: "sans" | "serif" | "display" | "handwritten" | "mono";
    weight?: "regular" | "medium" | "bold";
    note?: string;
  };
  mood?: string;
  overallConfidence: "high" | "medium" | "low";
}

/**
 * Ask the backend to read a reference image's styling. Advisory + fail-soft:
 * resolves `null` on any error (bad image, model failure, network) so the
 * onboarding wizard can proceed with brand defaults rather than blocking.
 */
export async function analyzeReferenceApi(dataUrl: string): Promise<StylingProfile | null> {
  try {
    const r = await apiFetch("/api/channels/analyze-reference", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ dataUrl }),
    });
    if (!r.ok) return null;
    const body = (await r.json()) as { profile?: StylingProfile | null };
    return body.profile ?? null;
  } catch {
    return null;
  }
}

export async function activateChannelApi(slug: string): Promise<{ activeSlug: string }> {
  const r = await apiFetch(`/api/channels/${encodeURIComponent(slug)}/activate`, { method: "POST" });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json() as Promise<{ activeSlug: string }>;
}

export async function deleteChannelApi(slug: string): Promise<void> {
  const r = await apiFetch(`/api/channels/${encodeURIComponent(slug)}`, { method: "DELETE" });
  if (!r.ok) {
    let msg = `${r.status} ${r.statusText}`;
    try { const b = await r.json(); if (b?.error) msg = String(b.error); } catch { /* ignore */ }
    throw new Error(msg);
  }
}

async function postChannelAction(slug: string, action: "archive" | "restore"): Promise<Channel> {
  const r = await apiFetch(`/api/channels/${encodeURIComponent(slug)}/${action}`, { method: "POST" });
  if (!r.ok) {
    let msg = `${r.status} ${r.statusText}`;
    try { const b = await r.json(); if (b?.error) msg = String(b.error); } catch { /* ignore */ }
    throw new Error(msg);
  }
  try {
    return ((await r.json()) as { channel: Channel }).channel;
  } catch {
    throw new Error("The server answered, but not with a brand record.");
  }
}

/** Archive = hide, never delete. 422's sentence is the one to show verbatim. */
export function archiveChannelApi(slug: string): Promise<Channel> { return postChannelAction(slug, "archive"); }
export function restoreChannelApi(slug: string): Promise<Channel> { return postChannelAction(slug, "restore"); }

/** Patch a channel's mutable fields (displayName, brand, features, voicePromptFragment).
 *  Used by Settings to toggle per-channel feature flags, e.g. autoScan. */
export async function updateChannelApi(
  slug: string,
  patch: { displayName?: string; features?: Partial<ChannelFeatures>; voicePromptFragment?: string; socialCta?: string },
): Promise<Channel> {
  const r = await apiFetch(`/api/channels/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!r.ok) {
    let msg = `${r.status} ${r.statusText}`;
    try { const b = await r.json(); if (b?.error) msg = String(b.error); } catch { /* ignore */ }
    throw new Error(msg);
  }
  return r.json() as Promise<Channel>;
}

export interface CacheStats {
  entries: number;
  bytes: number;
  oldestAgeMs: number | null;
  config: {
    dir: string;
    maxBytes: number;
    maxAgeDays: number;
  };
}

export async function getCacheStats(): Promise<CacheStats> {
  return getJSON<CacheStats>("/api/cache/stats");
}

export async function evictCache(): Promise<{ entries: number; bytes: number }> {
  const r = await apiFetch("/api/cache/evict", { method: "POST" });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json() as Promise<{ entries: number; bytes: number }>;
}

export interface CookiesStatus {
  exists: boolean;
  bytes: number;
  lines: number;
  cookieLines: number;
  domains: string[];
  updatedAt: number | null;
  header: string | null;
  /** Jar carries signed-in auth cookies (LOGIN_INFO/SAPISID). False = a
   * logged-out/visitor jar that YouTube bot-walls — surfaced as a loud
   * warning so a degraded jar can't hide behind "Active". */
  loggedIn: boolean;
  /** Did YouTube ACCEPT the jar last time something asked? null = unobserved.
   *  `loggedIn` is only a name check and stayed true through two dead-session
   *  outages — this is the one that catches them. */
  sessionLive?: boolean | null;
  sessionCheckedAt?: number | null;
  sessionReason?: string | null;
}

export async function getCookiesStatus(): Promise<CookiesStatus> {
  return getJSON<CookiesStatus>("/api/cookies");
}

export async function saveCookies(text: string): Promise<CookiesStatus> {
  const r = await apiFetch("/api/cookies", {
    method: "POST",
    headers: { "content-type": "text/plain; charset=utf-8" },
    body: text,
  });
  const body = (await r.json()) as CookiesStatus & { error?: string };
  if (!r.ok) throw new Error(body.error || `${r.status} ${r.statusText}`);
  return body;
}

export async function clearCookies(): Promise<CookiesStatus> {
  const r = await apiFetch("/api/cookies", { method: "DELETE" });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json() as Promise<CookiesStatus>;
}

// ---------- residential proxy (yt-dlp egress) -----------------------

/** Masked view of the configured proxy — the server never sends the URL or
 * its embedded credentials, only these fields. */
export interface ProxyStatus {
  configured: boolean;
  source: "settings" | "env" | null;
  scheme: string | null;
  host: string | null;
  port: number | null;
  hasAuth: boolean;
  updatedAt: number | null;
}

export async function getProxyStatus(): Promise<ProxyStatus> {
  return getJSON<ProxyStatus>("/api/proxy");
}

export async function saveProxy(url: string): Promise<ProxyStatus> {
  const r = await apiFetch("/api/proxy", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ url }),
  });
  const body = (await r.json()) as ProxyStatus & { error?: string };
  if (!r.ok) throw new Error(body.error || `${r.status} ${r.statusText}`);
  return body;
}

export async function clearProxy(): Promise<ProxyStatus> {
  const r = await apiFetch("/api/proxy", { method: "DELETE" });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json() as Promise<ProxyStatus>;
}

// ---------- audio/video sync guard (off | warn | block) -------------------

export type AvSyncMode = "off" | "warn" | "block";

export interface AvSyncGuardStatus {
  mode: AvSyncMode;
  source: "settings" | "env" | null;
  updatedAt: number | null;
}

export async function getAvSyncGuardStatus(): Promise<AvSyncGuardStatus> {
  return getJSON<AvSyncGuardStatus>("/api/av-sync-guard");
}

export async function saveAvSyncGuardMode(mode: AvSyncMode): Promise<AvSyncGuardStatus> {
  const r = await apiFetch("/api/av-sync-guard", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ mode }),
  });
  const body = (await r.json()) as AvSyncGuardStatus & { error?: string };
  if (!r.ok) throw new Error(body.error || `${r.status} ${r.statusText}`);
  return body;
}

// ---------- residential proxy POOL (Webshare API) -------------------

export interface PoolCounts {
  total: number;
  valid: number;
  healthy: number;
  flagged: number;
}

/** Masked Webshare status — the server never returns the API key itself. */
export interface WebshareStatus {
  configured: boolean;
  source: "settings" | "env" | null;
  savedAt: number | null;
  pool: PoolCounts;
}

export async function getWebshareStatus(): Promise<WebshareStatus> {
  return getJSON<WebshareStatus>("/api/webshare");
}

export async function saveWebshareKey(key: string): Promise<WebshareStatus> {
  const r = await apiFetch("/api/webshare", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ key }),
  });
  const body = (await r.json()) as WebshareStatus & { error?: string };
  if (!r.ok) throw new Error(body.error || `${r.status} ${r.statusText}`);
  return body;
}

export async function clearWebshareKey(): Promise<WebshareStatus> {
  const r = await apiFetch("/api/webshare", { method: "DELETE" });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json() as Promise<WebshareStatus>;
}

// ---------- sources library (auto-built from past jobs) -------------

export interface Source {
  /** YouTube canonical 11-char id. */
  videoId: string;
  /** Canonical youtube.com/watch?v=… URL. */
  url: string;
  /** Video title pulled from YouTube during clip.ts's source-meta step. */
  title?: string;
  /** YouTube's hqdefault.jpg — 480x360, always available. */
  thumbnailUrl: string;
  /** Distinct host names seen across every job that used this video. */
  hosts: string[];
  /** Distinct guest names — same dedup. */
  guests: string[];
  /** Distinct topic strings — same dedup. */
  topics: string[];
  /** How many jobs (= clips) came from this source. */
  clipCount: number;
  /** Unix ms of the earliest job. */
  firstUsedAt: number;
  /** Unix ms of the latest touched job — drives "recently used" sort. */
  lastUsedAt: number;
  /** Job slugs the source has powered, newest first. */
  jobIds: string[];
}

export async function listSources(): Promise<Source[]> {
  const r = await getJSON<{ items: Source[] }>("/api/sources");
  return r.items;
}

/**
 * Correct the speaker name for a single-clip source: writes the operator
 * override + a card-only re-render, and the corrected name then shows on Sources
 * + Library. Multi-clip sources are rejected server-side (their clips can have
 * different speakers) — edit those per-clip via `editSpeakerName` from Library.
 * Throws on error (invalid/generic name, multi-clip source, missing raw).
 */
export async function editSourceSpeaker(videoId: string, name: string): Promise<{ host: string; name: string }> {
  const r = await apiFetch(`/api/sources/${encodeURIComponent(videoId)}/speaker`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
  return r.json() as Promise<{ host: string; name: string }>;
}

// ---------- shared source Discovery (cross-channel pool) ----------

/** How one channel has "touched" a shared source. */
export interface DiscoverChannelTouch {
  slug: string;
  displayName: string;
  clips: number;
  scanned: boolean;
  bookmarked: boolean;
}

/** A shared source: every video ANY channel has clipped or scanned, with a
 *  per-channel breakdown + the active channel's own flags. Extends Source. */
export interface DiscoverSource extends Source {
  channels: DiscoverChannelTouch[];
  clippedByActive: boolean;
  scannedByActive: boolean;
  bookmarkedByActive: boolean;
}

/**
 * Fetch the shared Discover pool. Resolves `null` when the feature is off
 * (404 — kill-switch or a V1 single-channel deploy) so the UI simply hides the
 * Discover tab instead of erroring.
 */
export async function listDiscoverSources(): Promise<{ items: DiscoverSource[]; activeSlug: string } | null> {
  const r = await apiFetch("/api/sources/discover");
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json() as Promise<{ items: DiscoverSource[]; activeSlug: string }>;
}

/** Toggle the active channel's bookmark on a shared source. */
export async function setSourceBookmark(videoId: string, on: boolean): Promise<void> {
  const r = await apiFetch(`/api/sources/${encodeURIComponent(videoId)}/bookmark`, {
    method: on ? "POST" : "DELETE",
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

// ---------- scheduling (Buffer, decoupled) ----------

export type ChannelService =
  | "instagram" | "youtube" | "tiktok" | "twitter" | "pinterest"
  | "facebook" | "linkedin" | "threads" | "mastodon" | "bluesky" | "unknown";

export interface SchedulingChannel {
  id: string;
  service: ChannelService;
  name: string;
  rawService?: string;
}

export interface SchedulingStatus {
  connected: boolean;
  provider?: string;
  accountName?: string;
  accountId?: string;
  savedAt?: string;
}

export async function getSchedulingStatus(): Promise<SchedulingStatus> {
  return getJSON<SchedulingStatus>("/api/scheduling/status");
}

export async function connectScheduling(token: string): Promise<SchedulingStatus> {
  const r = await apiFetch("/api/scheduling/connect", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ provider: "buffer", token }),
  });
  const body = (await r.json()) as SchedulingStatus & { error?: string };
  if (!r.ok) throw new Error(body.error || `${r.status} ${r.statusText}`);
  return body;
}

export async function disconnectScheduling(): Promise<SchedulingStatus> {
  const r = await apiFetch("/api/scheduling/disconnect", { method: "DELETE" });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json() as Promise<SchedulingStatus>;
}

/** Connect Buffer for a SPECIFIC channel — writes the token to that channel's
 *  credentials file regardless of which channel is currently active. */
export async function connectChannelScheduling(slug: string, token: string): Promise<SchedulingStatus> {
  const r = await apiFetch(`/api/channels/${encodeURIComponent(slug)}/scheduling/connect`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ provider: "buffer", token }),
  });
  const body = (await r.json()) as SchedulingStatus & { error?: string };
  if (!r.ok) throw new Error(body.error || `${r.status} ${r.statusText}`);
  return body;
}

export async function listSchedulingChannels(): Promise<SchedulingChannel[]> {
  const r = await getJSON<{ channels: SchedulingChannel[] }>("/api/scheduling/channels");
  return r.channels;
}

/** Buffer publishing mode. "automatic" = direct publish (default);
 * "notification" = phone push at dueAt, operator finishes the post
 * manually in the target app. */
export type SchedulingMode = "automatic" | "notification";

export interface SchedulePostBody {
  dueAt: string;
  channelIds: string[];
  defaultText?: string;
  overrides?: Array<{ channelId: string; text?: string; title?: string; scheduling?: SchedulingMode }>;
}

export interface ScheduleResult {
  scheduledFor: string;
  results: Array<{ channelId: string; postId: string; text: string; title?: string }>;
  failures: Array<{ channelId: string; error: string }>;
}

export type ScheduledPostStatus = "draft" | "scheduled" | "published" | "failed";

export interface ScheduledPost {
  id: string;
  channelId: string;
  service: ChannelService;
  channelName: string;
  text: string;
  dueAt: string | null;
  sentAt: string | null;
  status: ScheduledPostStatus;
  videoUrl?: string;
  imageUrl?: string;
  providerUrl?: string;
  errorMessage?: string;
}

export interface ListScheduledPostsParams {
  from?: string;
  to?: string;
  channelIds?: string[];
  statuses?: ScheduledPostStatus[];
}

export interface ListSchedulingPostsResponse {
  posts: ScheduledPost[];
  range: { from: string; to: string };
  /** True when the server returned a cached payload because the live
   *  fetch was rate-limited. UI should show a "stale" banner. */
  stale?: boolean;
  /** Age of the cached payload in ms when `stale` is set. */
  staleAgeMs?: number;
  rateLimited?: boolean;
  /** Provider's declared throttle window, e.g. "24h" — useful copy for
   *  the stale banner. */
  rateLimitWindow?: string | null;
  /** No scheduler configured on this deployment (the server answers 412).
   *  An empty, non-error state — every new channel starts here. */
  notConnected?: boolean;
}

/**
 * Read scheduled / published / failed posts in a date range. Defaults to
 * the last 7 days through the next 60 days when `from` / `to` are omitted,
 * matching the server's default window. Provider-agnostic — backed by
 * whichever SocialScheduler is connected.
 *
 * Server-side caching: identical requests within 60 seconds return the
 * cached payload. When the provider rate-limits us the server still
 * returns 200 with `stale: true` rather than wiping the calendar.
 */
export async function listSchedulingPosts(
  params: ListScheduledPostsParams = {},
): Promise<ListSchedulingPostsResponse> {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.channelIds?.length) qs.set("channelIds", params.channelIds.join(","));
  if (params.statuses?.length) qs.set("statuses", params.statuses.join(","));
  const path = `/api/scheduling/posts${qs.toString() ? `?${qs}` : ""}`;
  const r = await apiFetch(path);
  const body = (await r.json()) as {
    posts?: ScheduledPost[];
    range?: { from: string; to: string };
    stale?: boolean;
    staleAgeMs?: number;
    rateLimited?: boolean;
    rateLimitWindow?: string | null;
    error?: string;
  };
  // 412 = no scheduler is configured on this deployment. That is the NORMAL
  // state of a brand-new channel, not a failure: every self-serve signup hit
  // it twice per Library visit and logged two console errors (QA D-5). An
  // empty calendar is the honest answer.
  if (r.status === 412) {
    const now = new Date().toISOString();
    return {
      posts: [],
      range: body.range ?? { from: now, to: now },
      notConnected: true,
    };
  }
  if (!r.ok) {
    const err = new Error(body.error || `${r.status} ${r.statusText}`);
    // Tag 429s so the calendar can distinguish them from generic errors.
    if (r.status === 429) (err as Error & { rateLimited?: boolean }).rateLimited = true;
    throw err;
  }
  return {
    posts: body.posts ?? [],
    range: body.range ?? { from: params.from ?? "", to: params.to ?? "" },
    stale: body.stale,
    staleAgeMs: body.staleAgeMs,
    rateLimited: body.rateLimited,
    rateLimitWindow: body.rateLimitWindow ?? null,
  };
}

/**
 * Move a scheduled post to a new dueAt. The scheduled time of day is
 * caller-supplied — most calendar drag-and-drop interactions keep the
 * existing time and only change the day, but the API itself is
 * day-and-time agnostic.
 */
export async function reschedulePost(id: string, dueAt: string): Promise<ScheduledPost> {
  const r = await apiFetch(`/api/scheduling/posts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ dueAt }),
  });
  const body = (await r.json()) as { post?: ScheduledPost; error?: string };
  if (!r.ok || !body.post) throw new Error(body.error || `${r.status} ${r.statusText}`);
  return body.post;
}

/** Per-channel Buffer errors that were non-fatal to a draft reschedule
 *  (e.g. one channel already published). Empty when every channel moved. */
export type ReschedulePerChannelError = { postId: string; error: string };

/**
 * Reschedule a LEGACY post-draft's every-channel Buffer post to a new time and
 * sync the local `scheduledFor`. Returns the updated draft plus any non-fatal
 * per-channel errors. A true edit — not a re-publish (no duplicate post).
 */
export async function rescheduleDraft(
  id: string,
  dueAt: string,
): Promise<{ draft: PostDraft; errors: ReschedulePerChannelError[] }> {
  const r = await apiFetch(`/api/post-drafts/${encodeURIComponent(id)}/reschedule`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ dueAt }),
  });
  const body = (await r.json()) as { draft?: PostDraft; errors?: ReschedulePerChannelError[]; error?: string };
  if (!r.ok || !body.draft) throw new Error(body.error || `${r.status} ${r.statusText}`);
  return { draft: body.draft, errors: body.errors ?? [] };
}

/**
 * Reschedule a BETA (AI-image) post-draft's every-channel Buffer post to a new
 * time and sync the local `publishedTo.dueAt`. True edit, not a re-publish.
 */
export async function rescheduleBetaDraft(
  id: string,
  dueAt: string,
): Promise<{ draft: BetaPostDraft; errors: ReschedulePerChannelError[] }> {
  const r = await apiFetch(`/api/post-drafts-beta/${encodeURIComponent(id)}/reschedule`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ dueAt }),
  });
  const body = (await r.json()) as { draft?: BetaPostDraft; errors?: ReschedulePerChannelError[]; error?: string };
  if (!r.ok || !body.draft) throw new Error(body.error || `${r.status} ${r.statusText}`);
  return { draft: body.draft, errors: body.errors ?? [] };
}

/**
 * "Schedule again" (2026-09-14): copy a PUBLISHED post into a fresh, un-scheduled
 * draft (same slides + text, `recycledFrom` stamped). The original is never
 * touched — publish is blocked on a published draft, so recycling is a copy.
 * Returns the new draft; the caller opens the normal Publish modal on it.
 */
export async function duplicatePostDraft(id: string): Promise<PostDraft> {
  const r = await apiFetch(`/api/post-drafts/${encodeURIComponent(id)}/duplicate`, { method: "POST" });
  const data = (await r.json().catch(() => ({}))) as { draft?: PostDraft; error?: string };
  if (!r.ok || !data.draft) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data.draft;
}

export async function duplicateBetaDraft(id: string): Promise<BetaPostDraft> {
  const r = await apiFetch(`/api/post-drafts-beta/${encodeURIComponent(id)}/duplicate`, { method: "POST" });
  const data = (await r.json().catch(() => ({}))) as { draft?: BetaPostDraft; error?: string };
  if (!r.ok || !data.draft) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data.draft;
}

/**
 * Permanently delete a scheduled post from the provider (Buffer) and the
 * calendar. Idempotent server-side, so re-deleting an already-gone post
 * resolves cleanly — the batch-delete loop and double-clicks rely on that.
 */
export async function deleteScheduledPost(id: string): Promise<void> {
  const r = await apiFetch(`/api/scheduling/posts/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    const err = new Error(body.error || `${r.status} ${r.statusText}`);
    if (r.status === 429) (err as Error & { rateLimited?: boolean }).rateLimited = true;
    throw err;
  }
}

export async function scheduleJobPost(id: string, body: SchedulePostBody): Promise<ScheduleResult> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/schedule`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const payload = (await r.json()) as ScheduleResult & { error?: string };
  if (!r.ok) throw new Error(payload.error || `${r.status} ${r.statusText}`);
  return payload;
}

export function videoUrl(id: string): string {
  return `/api/jobs/${encodeURIComponent(id)}/video`;
}

// ---------- Studio (iterate editor — Phase 1: player foundation) ----------

/** Everything the Studio screen needs to mount ClipComposition in
 *  @remotion/player: composition geometry + the render-identical inputProps
 *  (asset paths rewritten server-side to API URLs). */
export interface StudioInputs {
  clipName: string;
  fps: number;
  width: number;
  height: number;
  durationInFrames: number;
  /** Seconds of branded cover baked BEFORE content — cue times are content
   *  times, so seeks add this offset. 0 when the cover-hold is off. */
  coverHoldSec: number;
  /** The clip's EXPLICIT channel binding — null for legacy/no-channel clips
   *  or when channels are off. Gates the channel-promotion UI. */
  channelSlug: string | null;
  /** The bound channel's display name, for user-facing copy ("Court & Clair",
   *  never the slug). null whenever channelSlug is null. */
  channelDisplayName: string | null;
  inputProps: ClipProps;
  /** Measured caption fit is on (QL_MEASURED_FIT). The Studio's live gate on
   *  design commits is inert when false. Absent on older servers ⇒ off. */
  measuredFit?: boolean;
}

export async function getStudioInputs(id: string): Promise<StudioInputs> {
  return getJSON<StudioInputs>(`/api/clips/${encodeURIComponent(id)}/studio-inputs`);
}

/** Library card thumbnail for the latest render of a job. */
export function thumbUrl(id: string): string {
  return `/api/jobs/${encodeURIComponent(id)}/thumb`;
}

/** Library card thumbnail for a specific archived render. */
export function archivedThumbUrl(id: string, idx: number): string {
  return `/api/jobs/${encodeURIComponent(id)}/renders/${idx}/thumb`;
}

export interface RenderArchiveEntry {
  idx: number;
  archivedAt: number;
  reason: "reclip" | "caption-offset" | "trim" | "regenerate-captions" | "remix-audio" | "restore" | "edit-speaker" | "rerender" | "polish-camera" | "reframe" | "studio-approve";
  input?: JobInput;
  context?: Record<string, unknown>;
  hasVideo: boolean;
  hasCaption: boolean;
  hasSrt: boolean;
}

/** Fetch the past-render history for a job. Newest first. */
export async function listRenders(id: string): Promise<RenderArchiveEntry[]> {
  const r = await getJSON<{ items: RenderArchiveEntry[] }>(
    `/api/jobs/${encodeURIComponent(id)}/renders`,
  );
  return r.items;
}

export function archivedVideoUrl(id: string, idx: number): string {
  return `/api/jobs/${encodeURIComponent(id)}/renders/${idx}/video`;
}

export function archivedCaptionUrl(id: string, idx: number): string {
  return `/api/jobs/${encodeURIComponent(id)}/renders/${idx}/caption`;
}

/** Roll back: make archived render `idx` the current one. Append-only — the
 *  current render is archived first, so it's reversible (roll forward by
 *  restoring the entry that appears at the top of the history afterwards). */
export async function restoreRender(id: string, idx: number): Promise<void> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/renders/${idx}/restore`, { method: "POST" });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
}

/** Shift every cue in the saved SRT by `offsetMs` and re-render. Fast (~30s). */
export async function shiftCaptions(id: string, offsetMs: number): Promise<void> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/edit/caption-offset`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ offsetMs }),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

/**
 * Retry a failed/incomplete job with the same parameters it was started
 * with. Requires `work/<id>/input.json` to exist (written when the job
 * was originally spawned).
 */
export async function retryJob(id: string): Promise<void> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/retry`, { method: "POST" });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

/**
 * Render-ONLY re-run: reuses every cached artefact (clip, transcript,
 * captions incl. chat edits, music, speaker cards) and re-runs just the
 * Remotion render (~1-2 min). The cheap counterpart to retryJob's full
 * pipeline re-run. Works after a server restart.
 */
export async function rerenderJob(id: string): Promise<void> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/rerender`, { method: "POST" });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

/**
 * Cancel an in-flight job and/or wipe its traces.
 *   wipe=false → just SIGTERM the running child. Files stay.
 *   wipe=true  → kill + delete work/, raw/, out/ AND every archived past
 *                render for this id; storage copies (R2 render/thumb/source
 *                + mirrors) are cleaned up best-effort in the background.
 *                Irreversible — callers own the confirm UX. Slug freed.
 */
export async function discardJob(id: string, wipe: boolean): Promise<void> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}?wipe=${wipe ? "true" : "false"}`, {
    method: "DELETE",
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

/** Re-run only the social-caption stage (cheap, ~1 Claude call). */
export async function regenerateCaption(id: string): Promise<void> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/edit/regenerate-caption`, {
    method: "POST",
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

/**
 * Wipe the cached Arabic SRT and re-run translate + render. Uses the
 * existing Whisper transcript and speaker cuts, so the cost is one
 * Claude call (translate) plus a render (~30s–2min total). The previous
 * render is archived first — accessible from JobDetail history.
 */
export async function regenerateSubtitles(id: string): Promise<void> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/edit/regenerate-subtitles`, {
    method: "POST",
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

/**
 * Correct the on-screen speaker name of a rendered clip. Persists the operator
 * name as an authoritative override, archives the prior render (restorable), and
 * re-renders the card only (~30s–2min, no re-translate). The name is validated
 * server-side; an empty or generic placeholder throws. Returns the applied slug
 * + display name. The job moves to `rendering` — poll or subscribe as usual.
 */
export async function editSpeakerName(id: string, name: string): Promise<{ host: string; name: string }> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/edit/speaker`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
  return r.json() as Promise<{ host: string; name: string }>;
}

export interface MusicSettings {
  /** Last applied volume; defaults to `default` for clips rendered before the slider existed. */
  volume: number;
  /** Server-side default for fresh renders (mirror of ClipComposition's fallback). */
  default: number;
  min: number;
  max: number;
  /** What pickMusic actually settled on for the most recent run —
   *  read from work/<clip>/music.json. `null` when no music.json
   *  exists yet (pickMusic hasn't run, or legacy clip). Lets the UI
   *  show "Auto · resolved to <track>" once a render is done. */
  resolved: { track: string; reason: string; startOffsetSec: number } | null;
}

/**
 * Current music settings for the clip — the volume that was applied to
 * the latest render, plus the slider's bounds. The slider initialises
 * its position from `volume` so it reflects the actual audible state.
 */
export async function getMusicSettings(id: string): Promise<MusicSettings> {
  return getJSON<MusicSettings>(`/api/jobs/${encodeURIComponent(id)}/music`);
}

/**
 * Re-mix the audio of an existing render at a new music volume.
 * Video stream is stream-copied (no re-encode); the call runs ~5–20 s.
 * The previous render is archived first — accessible from JobDetail
 * history just like every other edit op.
 */
export async function regenerateMusicVolume(id: string, musicVolume: number): Promise<void> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/edit/music-volume`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ musicVolume }),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

/**
 * Wipe the stage cache and re-run the full pipeline with new
 * start/end-or-duration/phrase. Pass either `to` (timestamp) or
 * `duration` (seconds) — never both. Both omitted = AI auto-detect.
 */
export async function reclip(
  id: string,
  params: {
    from: string;
    to?: string;
    duration?: number;
    startPhrase?: string;
    endPhrase?: string;
    finePan?: boolean;
    /** Tri-state to match server-side ReclipParams.musicTrack:
     *    - `undefined` → keep prior pick (server reads input.json fallback).
     *    - `""`        → explicit Auto / let Claude pick.
     *    - filename    → switch to that track. */
    musicTrack?: string;
  },
): Promise<void> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/edit/reclip`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

/**
 * Camera polish: re-judges framing, pans and the opening shot from scratch
 * (fine-pan sampling) and re-renders. Captions, timing and music are reused
 * byte-identical — this is the "the camera is bad but everything else is
 * right" fix. No params: the server pins the exact source window from the
 * clip's persisted artifacts.
 */
export async function polishCamera(id: string): Promise<void> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/edit/polish-camera`, {
    method: "POST",
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

/** Swap the music TRACK on a finished render — ffmpeg-only remix (~5-20s),
 *  no re-render and no Claude call. Use instead of a full re-cut when the
 *  only thing wrong is the bed. */
export async function changeMusicTrack(id: string, track: string): Promise<void> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/edit/music-track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ track }),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

// ---------- reframe (operator framing overrides) ----------

export type FramingAction = "aim" | "freeze";

export interface FramingOverride {
  /** ABSOLUTE source seconds — survives a re-detect and a window change. */
  fromSec: number;
  toSec: number;
  action: FramingAction;
  centerX?: number;
  centerY?: number;
  zoom?: number;
  note?: string;
}

export interface ReframeShot {
  index: number;
  startSec: number;
  endSec: number;
  absStartSec: number;
  absEndSec: number;
  centerX: number;
  centerY: number;
  zoom: number;
  override: FramingAction | null;
}

export interface ReframeState {
  window: { sourceStartSec: number; sourceEndSec: number } | null;
  source: { available: boolean; width?: number; height?: number; reason?: string };
  shots: ReframeShot[];
  overrides: FramingOverride[];
  hasTrack: boolean;
  /** Baked cover-hold seconds the rendered mp4 carries before clip content —
   *  added to displayed times so they match the player's clock. */
  playerOffsetSec: number;
}

/** Shot bounds + current framing + saved overrides for the Reframe panel. */
export async function getFraming(id: string): Promise<ReframeState> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/framing`);
  const body = (await r.json()) as ReframeState & { error?: string };
  if (!r.ok) throw new Error(body.error || `${r.status} ${r.statusText}`);
  return body;
}

/** Replace the whole override list; returns what was actually stored
 *  (clamped, sorted, de-overlapped) so the UI shows reality, not intent. */
export async function saveFramingOverrides(
  id: string,
  overrides: FramingOverride[],
): Promise<FramingOverride[]> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/framing/overrides`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ overrides }),
  });
  const body = (await r.json()) as { overrides?: FramingOverride[]; error?: string };
  if (!r.ok) throw new Error(body.error || `${r.status} ${r.statusText}`);
  return body.overrides ?? [];
}

/** One FULL-SOURCE still at an absolute source second — what you aim against.
 *  Deliberately not a frame of the rendered clip: that one is already cropped. */
export function framingFrameUrl(id: string, absSec: number, width = 1280): string {
  return `/api/jobs/${encodeURIComponent(id)}/framing/frame?t=${absSec.toFixed(2)}&w=${width}`;
}

/** Apply the saved overrides and re-render (keeps the shot track — no Vision pass). */
export async function reframe(id: string): Promise<void> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/edit/reframe`, { method: "POST" });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

// ---------- cut & extend (trim) ----------

/**
 * Dry-run plan for a Cut & Extend edit. Deltas are CLIP-CONTENT seconds
 * (the mp4 timeline minus the baked cover hold):
 *   startDelta > 0 cuts the head; < 0 starts earlier (extend).
 *   endDelta   > 0 extends the tail; < 0 cuts it.
 * `startDelta=0&endDelta=0` is valid and returns the BASELINE plan —
 * current boundary words, current length, and `coverHoldSec` (which the
 * client needs to convert playhead time → clip time).
 */
export interface TrimPreview {
  ok: true;
  /** Estimated content length of the re-cut clip, in seconds. */
  newDurationEstSec: number;
  /** Seconds of branded cover baked at the head of the mp4 (0 when off). */
  coverHoldSec: number;
  /** How precisely the current source window was recovered. */
  windowSource: "clip-window" | "shots-cache" | "approx";
  /** First ~words the re-cut clip would start on (cut / unchanged starts only). */
  newFirstWords?: string;
  /** Last ~words the re-cut clip would end after (cut / unchanged ends only). */
  newLastWords?: string;
  warnings: string[];
  /** The reclip parameters the edit would run with (informational). */
  params: {
    from: string;
    to?: string;
    duration?: number;
    startPhrase?: string;
    endPhrase?: string;
  };
}

/**
 * Preview what a Cut & Extend re-cut would produce, without running it.
 * See {@link TrimPreview} for delta semantics; (0, 0) fetches the baseline.
 */
export async function trimPreview(
  id: string,
  startDelta: number,
  endDelta: number,
  smart?: SmartTrimFields,
): Promise<TrimPreview> {
  let qs = `startDelta=${encodeURIComponent(String(startDelta))}&endDelta=${encodeURIComponent(String(endDelta))}`;
  if (smart?.extendEndPhrase !== undefined && smart.extendEndsAtSourceSec !== undefined) {
    qs += `&extendEndPhrase=${encodeURIComponent(smart.extendEndPhrase)}&extendEndsAtSourceSec=${encodeURIComponent(String(smart.extendEndsAtSourceSec))}`;
  }
  if (smart?.extendStartPhrase !== undefined && smart.extendStartsAtSourceSec !== undefined) {
    qs += `&extendStartPhrase=${encodeURIComponent(smart.extendStartPhrase)}&extendStartsAtSourceSec=${encodeURIComponent(String(smart.extendStartsAtSourceSec))}`;
  }
  return getJSON<TrimPreview>(`/api/jobs/${encodeURIComponent(id)}/trim-preview?${qs}`);
}

/** Error that preserves the HTTP status so callers can branch on it —
 *  e.g. Cut & Extend maps 409 (job already re-rendering) to friendlier copy. */
export class HttpError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = "HttpError";
  }
}

/**
 * Apply a Cut & Extend edit: archives the current render and re-runs the
 * full pipeline (~5–10 min) at the shifted bounds. Deltas are clip-content
 * seconds (see {@link TrimPreview}); both-zero is rejected with 400.
 * `speakerName`, when present, rides the same re-cut — the operator override
 * is persisted server-side before the spawn so the ONE re-render also fixes
 * the card (speaker-only fixes go through {@link editSpeakerName} instead).
 * Throws {@link HttpError} — status 409 means the job is already live.
 */
export async function applyTrim(
  id: string,
  startDeltaSec: number,
  endDeltaSec: number,
  speakerName?: string,
  smart?: SmartTrimFields,
): Promise<void> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/edit/trim`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      startDeltaSec,
      endDeltaSec,
      ...(speakerName ? { speakerName } : {}),
      ...(smart ?? {}),
    }),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new HttpError(body.error || `${r.status} ${r.statusText}`, r.status);
  }
}

// ---------- smart extend ----------

/** Smart Extend fields that ride a trim. Each direction is both-or-neither and
 *  extend-only; the server re-verifies every pick against the CURRENT window. */
export interface SmartTrimFields {
  extendEndPhrase?: string;
  extendEndsAtSourceSec?: number;
  extendStartPhrase?: string;
  extendStartsAtSourceSec?: number;
}

/** Server shape of GET /api/jobs/:id/extend-context (src/server/extendPlan.ts).
 *  available:false means the Smart extend row must not render — the reason
 *  says why (flag off, upload-mode clip, no cached transcript, no headroom). */
export interface ExtendContext {
  available: boolean;
  reason?: "flag-off" | "upload-mode" | "no-transcript" | "no-length" | "no-headroom";
  windowSource?: "clip-window" | "shots-cache" | "approx";
  maxClipSec: number;
  currentLenSec?: number;
  headroomSec?: number;
  /** Absolute source second where the current clip ends. */
  sourceEndSec?: number;
  /** Source-transcript lines past the cut (headroom + a grayed display margin). */
  followOn?: Array<{ startSec: number; endSec: number; text: string }>;
  /** Absolute source second where the current clip starts. */
  sourceStartSec?: number;
  /** Seconds the clip may start EARLIER (0 at the source head). */
  backHeadroomSec?: number;
  /** Source-transcript lines BEFORE the current start (headroom + margin). */
  leadIn?: Array<{ startSec: number; endSec: number; text: string }>;
  warnings: string[];
}

/** One AI-suggested opening, already verified verbatim + re-anchored server-side. */
export interface OpeningSuggestion {
  startPhrase: string;
  reason: string;
  startsAtSourceSec: number;
  addedSec: number;
  newDurationSec: number;
}

/** One AI-suggested ending, already verified verbatim + re-anchored server-side. */
export interface ExtendSuggestion {
  endPhrase: string;
  reason: string;
  endsAtSourceSec: number;
  addedSec: number;
  newDurationSec: number;
}

export async function extendContext(id: string): Promise<ExtendContext> {
  return getJSON<ExtendContext>(`/api/jobs/${encodeURIComponent(id)}/extend-context`);
}

export async function extendSuggest(
  id: string,
): Promise<{ suggestions: ExtendSuggestion[]; warnings: string[] }> {
  return suggestForDirection<ExtendSuggestion>(id, "end");
}

/** The backwards half — asks where the clip should START instead. */
export async function openingSuggest(
  id: string,
): Promise<{ suggestions: OpeningSuggestion[]; warnings: string[] }> {
  return suggestForDirection<OpeningSuggestion>(id, "start");
}

/** Resolve a phrase the operator CLICKED into a verified pick (no AI call).
 *  Throws HttpError 422 with an operator-facing reason when the click can't
 *  be honoured — the same guards the AI suggestions pass. */
export async function extendResolve<T>(
  id: string,
  direction: "start" | "end",
  phrase: string,
): Promise<T> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/extend-resolve`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ direction, phrase }),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new HttpError(body.error || `${r.status} ${r.statusText}`, r.status);
  }
  return ((await r.json()) as { suggestion: T }).suggestion;
}

async function suggestForDirection<T>(
  id: string,
  direction: "start" | "end",
): Promise<{ suggestions: T[]; warnings: string[] }> {
  const r = await apiFetch(`/api/jobs/${encodeURIComponent(id)}/extend-suggest`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ direction }),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new HttpError(body.error || `${r.status} ${r.statusText}`, r.status);
  }
  return (await r.json()) as { suggestions: T[]; warnings: string[] };
}

// ---------- backlog ----------

export interface BacklogEntry {
  id: string;
  createdAt: number;
  label?: string;
  notes?: string;
  url?: string;
  from?: string;
  to?: string;
  duration?: number;
  startPhrase?: string;
  endPhrase?: string;
  host?: string;
  guests?: string[];
  topic?: string;
  /** Provenance of `host`: "operator" (typed in the form — authoritative,
   *  forwarded as JobInput.hostSource so the render locks the name) or "scan"
   *  (model guess — correctable). Undefined on manual / legacy rows. */
  hostSource?: 'operator' | 'scan';
  /** Operator's background-music pick for this candidate (filename, or ""/absent
   *  = Auto). Inherited from the New Job form on scan/locate, editable per-scene
   *  in the Backlog; SceneRow forwards it to createJob so the render honors it. */
  musicTrack?: string;
  // --- scan-candidate enrichment (Phase 2 fields; absent on manual entries) ---
  origin?: 'manual' | 'scan' | 'locate';
  candidate?: boolean;
  hookEn?: string;
  hookAr?: string;
  whyItWorks?: string;
  /** 2–3 sentence operator-facing summary of the scene's arc (scan/locate). */
  summary?: string;
  /** Verbatim closing line that resolves the scene. */
  payoffLine?: string;
  /** The takeaway stated as a one-sentence insight. */
  resolutionInsight?: string;
  /** Ending-judge annotation: did the scene end on a real resolution? */
  endingVerdict?: { verdict: 'keep' | 'extended' | 'warn'; score: number; reason: string };
  confidence?: number;
  sourceTitle?: string;
  videoId?: string;
  sceneTranscript?: string;
  renderedJobId?: string;
  /** Operator shelved this scene without deleting it — it stays reachable
   *  under the Backlog's "Archived" filter chip (never-silently-vanish). */
  archived?: boolean;
  /** When the entry was (last) archived — ms epoch. Cleared on restore. */
  archivedAt?: number;
  /** Owning channel slug (absent on legacy/untagged + single-channel rows;
   *  resolves to the default channel when missing). Used by the Scene
   *  Library modal's per-row channel chip when CHANNELS_ENABLED. */
  channelSlug?: string;
  /** Boundary-adjustment record (SceneDetailModal "Adjust scene"). Present ⇔
   *  the operator moved this scene's window; the live from/to/startPhrase/
   *  endPhrase hold the ADJUSTED values while `original` keeps the scanner's
   *  pre-adjustment pick (captured once, on the first adjustment) — powers
   *  the original-vs-adjusted display + "reset to scanner's pick". */
  adjusted?: {
    original: { from?: string; to?: string; startPhrase?: string; endPhrase?: string; noSnap?: boolean };
    adjustedAt: number;
  };
  /** Render the start verbatim (forwarded as JobInput.noSnap). Set when the
   *  operator seconds-nudged the start; cleared by phrase re-anchor + reset. */
  noSnap?: boolean;
}

export type BacklogDraft = Omit<BacklogEntry, "id" | "createdAt">;

/** A backlog entry that belongs on the "Scanned scenes" shelf: produced by a
 *  scan or locate. Rendered scenes count too — rendering a scene flips
 *  `candidate:false`, but the row must stay visible (with an "Open render"
 *  action) instead of vanishing from the only UI that lists scenes. Shared by
 *  the Backlog screen's inline list so scanned scenes are always visible. */
export function isScannedScene(e: BacklogEntry): boolean {
  return e.origin === "scan" || e.origin === "locate";
}

/** A scanned scene that has already been rendered (carries the job id of the
 *  render it produced). Drives the inline list's "Open render" / "Render again"
 *  affordance and lets the sidebar "waiting to render" badge count only the
 *  un-rendered scenes. */
export function isRenderedScene(e: BacklogEntry): boolean {
  return !!e.renderedJobId;
}

/**
 * Fetch the backlog plus the additive cross-channel hint.
 *
 * `otherChannelScannedCount` is the number of scanned scenes (scan/locate +
 * still a candidate) that live under OTHER channels — surfaced by the Scene
 * Library modal so the operator never concludes scenes were lost (SCAN-12).
 * It's 0 when CHANNELS_ENABLED is off (single-channel setups). Callers that
 * only want the entries destructure `const { items } = await listBacklog()`.
 */
export async function listBacklog(): Promise<{ items: BacklogEntry[]; otherChannelScannedCount: number }> {
  const r = await getJSON<{ items: BacklogEntry[]; otherChannelScannedCount?: number }>("/api/backlog");
  return { items: r.items, otherChannelScannedCount: r.otherChannelScannedCount ?? 0 };
}

export async function addBacklog(input: BacklogDraft): Promise<BacklogEntry> {
  const r = await apiFetch("/api/backlog", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json() as Promise<BacklogEntry>;
}

export async function updateBacklog(id: string, patch: BacklogDraft): Promise<BacklogEntry> {
  const r = await apiFetch(`/api/backlog/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json() as Promise<BacklogEntry>;
}

export async function deleteBacklog(id: string): Promise<void> {
  const r = await apiFetch(`/api/backlog/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
}

/**
 * Archive (or restore) ONE scanned scene — archive-not-delete, so the row
 * just moves between the Backlog's Fresh and Archived filter chips. Sends a
 * partial PATCH: the server whitelist only touches keys the body carries, and
 * it stamps/clears `archivedAt` alongside the flag.
 */
export async function setBacklogArchived(id: string, archived: boolean): Promise<BacklogEntry> {
  const r = await apiFetch(`/api/backlog/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(archived ? { archived, archivedAt: Date.now() } : { archived }),
  });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json() as Promise<BacklogEntry>;
}

/**
 * Archive/restore a whole set of scenes in one request (the per-group
 * "Archive remaining" / "Restore all" actions). The server applies all ids
 * in a single serialized read-modify-write and reports how many it touched.
 */
export async function bulkArchiveBacklog(ids: string[], archived: boolean): Promise<{ ok: boolean; updated: number }> {
  const r = await apiFetch("/api/backlog/bulk-archive", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ ids, archived }),
  });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json() as Promise<{ ok: boolean; updated: number }>;
}

// ---------- backlog: boundary adjust (SceneDetailModal "Adjust scene") ----------

/** One boundary-adjust action, mirrored by the server's parseAdjustAction. */
export type AdjustSceneAction =
  | { action: "nudge"; edge: "start" | "end"; deltaSec: number }
  | { action: "anchor"; edge: "start" | "end"; phrase: string }
  | { action: "reset" };

export interface AdjustSceneResponse {
  /** False = domain outcome (phrase not found / nonsense window): nothing was
   *  mutated and `message` is the inline text to show the operator. */
  found: boolean;
  message?: string;
  entry: BacklogEntry;
  /** A nudge clamp fired (hard cap / t=0 floor / min window). */
  clamped?: boolean;
  /** The adjusted window exceeds the hard cap — render will trim to the cap. */
  overCap?: boolean;
  /** Anchor details: the transcript's own wording that was snapped to. */
  match?: { matchedText: string; exact: boolean; extended: boolean; boundarySec: number };
}

export async function adjustScene(id: string, action: AdjustSceneAction): Promise<AdjustSceneResponse> {
  const r = await apiFetch(`/api/backlog/${encodeURIComponent(id)}/adjust`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(action),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
  return r.json() as Promise<AdjustSceneResponse>;
}

/** Pre-flight for the adjust section: is semantic phrase re-anchoring possible
 *  (durable transcript cache present), and what's the server's hard clip cap. */
export async function getAdjustContext(id: string): Promise<{ transcriptAvailable: boolean; maxClipSec: number }> {
  return getJSON<{ transcriptAvailable: boolean; maxClipSec: number }>(
    `/api/backlog/${encodeURIComponent(id)}/adjust-context`,
  );
}

// ---------- requests: durable log of every scan + render (ADR-047) ----------

export type RequestStatus = "queued" | "transcribing" | "scanning" | "scanned" | "failed" | "interrupted";

export interface RequestRecord {
  id: string;
  kind: "scan" | "locate";
  status: RequestStatus;
  url: string;
  videoId?: string;
  /** Candidates this scan wrote to the Backlog at finish time. */
  candidateCount?: number;
  /** Human-readable outcome ("Added 2 scenes", "Already rendered", …) so a
   *  finished request is never just an opaque "0 candidates". */
  detail?: string;
  /** When the result duplicated an already-rendered scene, that render's job id
   *  — the UI offers "Open render" instead of dead-ending. */
  relatedJobId?: string;
  errorMsg?: string;
  channelSlug?: string;
  createdAt: number;
  finishedAt?: number;
  // Enriched from the live Backlog (joined by videoId):
  /** Readable video title, if known. */
  sourceTitle?: string;
  /** First guest (else host) from the scan's candidates. */
  speaker?: string;
  /** Candidates still on the shelf (not yet rendered). */
  pendingCount: number;
}

export async function listRequests(): Promise<RequestRecord[]> {
  const r = await getJSON<{ items: RequestRecord[] }>("/api/requests");
  return r.items ?? [];
}

export async function deleteRequest(id: string): Promise<void> {
  const r = await apiFetch(`/api/requests/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
}

export async function batchDeleteRequests(ids: string[]): Promise<number> {
  const r = await apiFetch("/api/requests/batch-delete", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ ids }),
  });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  const j = (await r.json()) as { removed?: number };
  return j.removed ?? 0;
}

/** Re-run a past scan/locate from its persisted record (URL + original inputs).
 *  Returns the new scan id. The original request stays in the log as history. */
export async function retryRequest(id: string): Promise<{ scanId: string }> {
  const r = await apiFetch(`/api/requests/${encodeURIComponent(id)}/retry`, { method: "POST" });
  if (!r.ok) {
    let msg = `${r.status} ${r.statusText}`;
    try { const j = (await r.json()) as { error?: string }; if (j.error) msg = j.error; } catch { /* keep status text */ }
    throw new Error(msg);
  }
  return (await r.json()) as { scanId: string };
}

// ---------- scan (auto-scene-finder Phase 2/3) ----------

export interface ScanResult {
  /** Server record uses `id`; createScan's POST response uses `scanId`. */
  id?: string;
  scanId?: string;
  videoId: string;
  /** Original URL + locate inputs (set for /api/locate) — used to re-edit a failed locate. */
  url?: string;
  openingLine?: string;
  closingLine?: string;
  status: 'queued' | 'transcribing' | 'scanning' | 'done' | 'failed';
  /** 0–1 coarse progress for the in-flight scan. */
  progress?: number;
  /** Human-readable current step, e.g. "Scanning window 2/4…". */
  detail?: string;
  /** Candidates written to the Backlog on success. */
  candidateCount?: number;
  /** When a locate deduped against an ALREADY-RENDERED scene: that render's job id. */
  relatedJobId?: string;
  /** When a locate deduped against an existing Backlog scene: that entry's id —
   *  lets the UI highlight the scene instead of reporting a bare "0 found". */
  relatedEntryId?: string;
  /** Error detail on failure. */
  errorMsg?: string;
}

/** Kick off an async scene scan. Returns immediately with scanId + videoId.
 *  403 when channel.features.autoScan is false.
 *  channelSlug is optional — the server resolves the active channel when absent.
 *  count is optional — defaults to scanConfig.defaultCount; clamped server-side to [1, maxCount]. */
export async function createScan(
  url: string,
  channelSlug?: string,
  count?: number,
  musicTrack?: string,
  opts?: { host?: string; guests?: string[]; topic?: string },
): Promise<{ scanId: string; videoId: string }> {
  const r = await apiFetch('/api/scan', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ url, channelSlug, count, musicTrack, ...opts }),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
  return r.json() as Promise<{ scanId: string; videoId: string }>;
}

/** Kick off a targeted "find this scene" locate. Returns scanId + videoId immediately;
 *  progress streams over the SAME /api/scan/:id/events as a scan. 403 when autoScan is off. */
export async function createLocate(
  url: string,
  openingLine: string,
  closingLine?: string,
  opts?: { channelSlug?: string; host?: string; guests?: string[]; topic?: string; musicTrack?: string },
): Promise<{ scanId: string; videoId: string }> {
  const r = await apiFetch('/api/locate', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ url, openingLine, closingLine, ...opts }),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
  return r.json() as Promise<{ scanId: string; videoId: string }>;
}

/** Poll a scan's current state (candidates available once status==='done'). */
export async function getScan(id: string): Promise<ScanResult> {
  return getJSON<ScanResult>(`/api/scan/${encodeURIComponent(id)}`);
}

/**
 * Open an EventSource for live job updates. The handler receives parsed
 * `update` and `log` events; the caller is responsible for closing.
 */
export function subscribeJob(
  id: string,
  on: { update?: (job: Job) => void; log?: (line: JobLogLine) => void; error?: (msg: string) => void },
): EventSource {
  const es = new EventSource(`/api/jobs/${encodeURIComponent(id)}/events`);
  es.addEventListener("update", (ev) => {
    try { on.update?.(JSON.parse((ev as MessageEvent).data) as Job); } catch { /* ignore */ }
  });
  es.addEventListener("log", (ev) => {
    try { on.log?.(JSON.parse((ev as MessageEvent).data) as JobLogLine); } catch { /* ignore */ }
  });
  es.addEventListener("error", (ev) => {
    on.error?.((ev as MessageEvent).data || "stream error");
  });
  return es;
}

/** Open an EventSource for live scan updates. Clone of subscribeJob.
 *  The caller is responsible for calling .close() when done. */
export function subscribeScan(
  id: string,
  on: { update?: (result: ScanResult) => void; error?: (msg: string) => void },
): EventSource {
  const es = new EventSource(`/api/scan/${encodeURIComponent(id)}/events`);
  es.addEventListener('update', (ev) => {
    try { on.update?.(JSON.parse((ev as MessageEvent).data) as ScanResult); } catch { /* ignore */ }
  });
  es.addEventListener('error', (ev) => {
    on.error?.((ev as MessageEvent).data || 'stream error');
  });
  return es;
}

// ---------- post-pipeline drafts --------------------------------------

export type PostDraftStatus =
  | "generating-text"
  | "pending"
  | "scheduled"
  | "published"
  | "discarded"
  | "generation-failed"
  | "render-failed";

/** Background action currently holding a draft — server-persisted, so the
 *  card can show progress that survives refresh and navigation. */
export interface PostDraftBusy {
  action: "generate" | "regenerate" | "quote-shorter" | "quote-longer";
  startedAt: number;
}

/** Outcome of the last background action on a draft. */
export interface PostDraftNotice {
  message: string;
  at: number;
}

export interface PostContent {
  cover_ar: string;
  cover_en: string;
  body_ar: string;
  body_en: string;
  rationale: string;
}

export interface PostDraft {
  id: string;
  theme: string;
  content: PostContent;
  generatedAt: number;
  status: PostDraftStatus;
  errorMessage?: string;
  bufferPostId?: string;
  bufferPostIds?: string[];
  scheduledFor?: string;
  busy?: PostDraftBusy;
  notice?: PostDraftNotice;
  /** Set on a copy made by "Schedule again" on a published post — the original's id. */
  recycledFrom?: string;
}

export async function listPostDrafts(status?: PostDraftStatus): Promise<PostDraft[]> {
  const path = status
    ? `/api/post-drafts?status=${encodeURIComponent(status)}`
    : "/api/post-drafts";
  const r = await getJSON<{ drafts: PostDraft[] }>(path);
  return r.drafts;
}

export async function generatePostDraft(
  theme?: string,
  opts: { seedMode?: BetaSeedMode } = {},
): Promise<PostDraft> {
  const payload: { theme?: string; seedMode?: BetaSeedMode } = {};
  if (theme) payload.theme = theme;
  if (opts.seedMode) payload.seedMode = opts.seedMode;
  const r = await apiFetch("/api/post-drafts/generate", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = (await r.json()) as { draft?: PostDraft; error?: string };
  if (!r.ok || !body.draft) throw new Error(body.error || `${r.status} ${r.statusText}`);
  return body.draft;
}

/**
 * Regenerate a Legacy post draft. With no `feedback`, it re-rolls a fresh
 * version (around the same theme/seed). With `feedback`, the operator's note
 * is injected into the generation prompt so the re-roll addresses it.
 */
export async function regeneratePostDraft(
  id: string,
  opts: { feedback?: string } = {},
): Promise<PostDraft> {
  const feedback = opts.feedback?.trim();
  const r = await apiFetch(
    `/api/post-drafts/${encodeURIComponent(id)}/regenerate`,
    feedback
      ? {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ feedback }),
        }
      : { method: "POST" },
  );
  const body = (await r.json()) as { draft?: PostDraft; error?: string };
  if (!r.ok || !body.draft) throw new Error(body.error || `${r.status} ${r.statusText}`);
  return body.draft;
}

/**
 * Re-cut a Legacy draft's COVER QUOTE shorter or longer (~15% per press),
 * leaving the reflection body untouched, then re-render the slides.
 *
 * A 409 is the expected "nothing changed" answer — the quote is at the end of
 * its render-safe range, or the rewrite missed the bar and the original was
 * kept. Its `error` text is already operator-facing, so show it as-is.
 */
export async function adjustPostQuoteLength(
  id: string,
  direction: "shorter" | "longer",
): Promise<PostDraft> {
  const r = await apiFetch(`/api/post-drafts/${encodeURIComponent(id)}/quote-length`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ direction }),
  });
  const body = (await r.json()) as { draft?: PostDraft; error?: string };
  if (!r.ok || !body.draft) throw new Error(body.error || `${r.status} ${r.statusText}`);
  return body.draft;
}

export async function discardPostDraft(id: string): Promise<void> {
  const r = await apiFetch(`/api/post-drafts/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

export function postSlideUrl(id: string, slide: "cover" | "ar-body" | "en-body"): string {
  return `/api/post-drafts/${encodeURIComponent(id)}/${slide}`;
}

export interface ApprovePostBody {
  dueAt: string;
  channelIds?: string[];
}

export async function approvePostDraft(
  id: string,
  body: ApprovePostBody,
): Promise<PostDraft> {
  const r = await apiFetch(`/api/post-drafts/${encodeURIComponent(id)}/approve`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await r.json()) as { draft?: PostDraft; error?: string };
  if (!r.ok || !data.draft) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data.draft;
}

export interface PostPublishResult {
  channelId: string;
  service: string;
  channelName?: string;
  postId?: string;
  providerUrl?: string;
  error?: string;
}

/** Fetch the default Publish-modal caption for the Legacy pipeline. */
export async function getPostCaptionTemplate(): Promise<string> {
  const r = await apiFetch("/api/post-drafts/caption-template");
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  const data = (await r.json()) as { template: string };
  return data.template;
}

/**
 * Publish a Legacy post draft's 3 slides to Buffer (Instagram + Twitter
 * get the full carousel; Pinterest gets the first slide only). Mirrors
 * publishBetaDraft: the backend returns 502 with { draft, results } when
 * every channel failed, so we surface the per-channel errors to the modal
 * rather than throwing the bare "502 Bad Gateway".
 */
export async function publishPostDraft(
  id: string,
  body: {
    caption: string;
    dueAt: string;
    channelIds: string[];
    scheduling?: Record<string, SchedulingMode>;
    /** Per-post opt-out from the automatic CTA appendix. */
    skipCta?: boolean;
  },
): Promise<{ draft: PostDraft; results: PostPublishResult[] }> {
  const r = await apiFetch(`/api/post-drafts/${encodeURIComponent(id)}/publish`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await r.json().catch(() => ({}))) as {
    draft?: PostDraft;
    results?: PostPublishResult[];
    error?: string;
  };
  if (data.draft && Array.isArray(data.results)) {
    return { draft: data.draft, results: data.results };
  }
  if (!r.ok) throw new Error(data.error || `${r.status} ${r.statusText}`);
  if (!data.draft) throw new Error("publish response missing draft");
  return { draft: data.draft, results: data.results ?? [] };
}

// ---------- editable default post caption (both pipelines) ------------

export type PostPipelineId = "legacy" | "beta";

export interface DefaultCaption {
  template: string;
  /** "custom" = operator override saved (persisted under data/); "default" = bundled. */
  source: "custom" | "default";
}

function captionTemplatePath(pipeline: PostPipelineId): string {
  return pipeline === "beta"
    ? "/api/post-drafts-beta/caption-template"
    : "/api/post-drafts/caption-template";
}

function toDefaultCaption(d: Partial<DefaultCaption>, fallbackTemplate = ""): DefaultCaption {
  return {
    template: d.template ?? fallbackTemplate,
    source: d.source === "custom" ? "custom" : "default",
  };
}

/** Fetch the editable default caption + whether it's a custom override. */
export async function getDefaultCaption(pipeline: PostPipelineId): Promise<DefaultCaption> {
  const r = await apiFetch(captionTemplatePath(pipeline));
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return toDefaultCaption((await r.json()) as Partial<DefaultCaption>);
}

/** Save the operator's default caption for a pipeline (persists to data/). */
export async function setDefaultCaption(pipeline: PostPipelineId, template: string): Promise<DefaultCaption> {
  const r = await apiFetch(captionTemplatePath(pipeline), {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ template }),
  });
  const d = (await r.json().catch(() => ({}))) as Partial<DefaultCaption> & { error?: string };
  if (!r.ok) throw new Error(d.error || `${r.status} ${r.statusText}`);
  return toDefaultCaption(d, template);
}

/** Reset the default caption back to the bundled default. */
export async function resetDefaultCaption(pipeline: PostPipelineId): Promise<DefaultCaption> {
  const r = await apiFetch(captionTemplatePath(pipeline), { method: "DELETE" });
  const d = (await r.json().catch(() => ({}))) as Partial<DefaultCaption> & { error?: string };
  if (!r.ok) throw new Error(d.error || `${r.status} ${r.statusText}`);
  return toDefaultCaption(d);
}

// ---------- CTA slides library (legacy publish appendix) --------------

export interface CtaSet {
  id: string;
  label: string;
  /** 1–2 slide image filenames, in append order. */
  slides: string[];
  enabled: boolean;
  createdAt: string;
}

export interface CtaConfig {
  /** Master switch — appended automatically on every legacy publish when on. */
  enabled: boolean;
  platforms: { instagram: boolean; linkedin: boolean; twitter: boolean };
  cursor: number;
  ctas: CtaSet[];
  /** Which CTA the NEXT publish will append (ignores the master switch). */
  next: { id: string; index: number } | null;
}

export async function getCtaConfig(): Promise<CtaConfig> {
  const r = await apiFetch("/api/cta-slides");
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return (await r.json()) as CtaConfig;
}

/** Partial toggle update — master switch, platform ticks, per-CTA enable. */
export async function updateCtaConfig(patch: {
  enabled?: boolean;
  platforms?: Partial<{ instagram: boolean; linkedin: boolean; twitter: boolean }>;
  ctas?: Array<{ id: string; enabled?: boolean; label?: string }>;
}): Promise<CtaConfig> {
  const r = await apiFetch("/api/cta-slides", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  const data = (await r.json().catch(() => ({}))) as CtaConfig & { error?: string };
  if (!r.ok) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data;
}

/** Add a CTA from 1–2 local image files (read as data: URLs). */
export async function createCta(
  label: string,
  images: Array<{ filename: string; dataUrl: string }>,
): Promise<CtaSet> {
  const r = await apiFetch("/api/cta-slides", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ label, images }),
  });
  const data = (await r.json().catch(() => ({}))) as { cta?: CtaSet; error?: string };
  if (!r.ok || !data.cta) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data.cta;
}

export async function deleteCta(id: string): Promise<void> {
  const r = await apiFetch(`/api/cta-slides/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

export function ctaSlideUrl(id: string, file: string): string {
  return `/api/cta-slides/${encodeURIComponent(id)}/${encodeURIComponent(file)}`;
}

// ---------- post-pipeline-beta (atmospheric image carousel) -----------

export type BetaPostDraftStatus =
  | "generating-text"
  | "awaiting-image"
  | "rendering"
  | "ready-for-review"
  | "scheduled"
  | "published"
  | "discarded"
  /** Text generation failed — persisted with `errorMessage` so the card shows
   *  why and offers Retry (parity with the Legacy pipeline). */
  | "generation-failed";

export interface BetaPostContent {
  quote: string;
  arabicQuote: string;
  arabic: string;
  english: string;
  promptHint: string;
  vibe: string;
  attribution?: string;
}

export interface BetaPostDraft {
  id: string;
  theme: string;
  content: BetaPostContent;
  status: BetaPostDraftStatus;
  /** Real failure reason when status === "generation-failed". */
  errorMessage?: string;
  generatedAt: number;
  imageFilename?: string;
  imageSourceUrl?: string;
  /** Content version — bumps on each regenerate-text. Absent === 1. */
  version?: number;
  /** Operator-pinned per-slide font-size overrides (px); absent → auto-fit. */
  slideOverrides?: {
    coverArFontSize?: number;
    coverEnFontSize?: number;
  };
  /** Inspiration tile ids passed as visual references on the last
   *  GPT Image generation. UI uses this to badge the post and to
   *  default the next regenerate's modal to the same selection. */
  usedInspirationIds?: string[];
  /** Background action currently holding this draft (quote-length). */
  busy?: { action: "quote-shorter" | "quote-longer"; startedAt: number };
  /** Outcome of the last background action. */
  notice?: PostDraftNotice;
  /** Set on a copy made by "Schedule again" on a published post — the original's id. */
  recycledFrom?: string;
  /** Buffer publish bookkeeping. Set once the operator hits Publish. */
  publishedTo?: {
    dueAt: string;
    caption: string;
    results: Array<{
      channelId: string;
      service: string;
      channelName?: string;
      postId?: string;
      providerUrl?: string;
      error?: string;
    }>;
    publishedAt: number;
  };
}

/** Patch body for the inline-edit PATCH endpoint. */
export interface BetaDraftPatch {
  content?: Partial<BetaPostContent>;
  slideOverrides?: {
    /** Pass null/0 to clear the pin and revert to auto-fit. */
    coverArFontSize?: number | null;
    coverEnFontSize?: number | null;
  };
}

export async function patchBetaDraft(id: string, patch: BetaDraftPatch): Promise<BetaPostDraft> {
  const r = await apiFetch(`/api/post-drafts-beta/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  const data = (await r.json()) as { draft?: BetaPostDraft; error?: string };
  if (!r.ok || !data.draft) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data.draft;
}

export interface BetaPublishResult {
  channelId: string;
  service: string;
  channelName?: string;
  postId?: string;
  providerUrl?: string;
  error?: string;
}

/** Fetch the default caption template (with `{{topic}}` placeholder). */
export async function getBetaCaptionTemplate(): Promise<string> {
  const r = await apiFetch("/api/post-drafts-beta/caption-template");
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  const data = (await r.json()) as { template: string };
  return data.template;
}

export async function publishBetaDraft(
  id: string,
  body: {
    caption: string;
    dueAt: string;
    channelIds: string[];
    scheduling?: Record<string, SchedulingMode>;
  },
): Promise<{ draft: BetaPostDraft; results: BetaPublishResult[] }> {
  const r = await apiFetch(`/api/post-drafts-beta/${encodeURIComponent(id)}/publish`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await r.json().catch(() => ({}))) as {
    draft?: BetaPostDraft;
    results?: BetaPublishResult[];
    error?: string;
  };
  // The backend returns 502 with { draft, results } when every channel
  // failed (e.g. Buffer rejected the request). Surface those per-channel
  // errors to the caller rather than throwing the generic "502 Bad
  // Gateway" — the modal needs the result list to show the operator
  // exactly which channel failed and why.
  if (data.draft && Array.isArray(data.results)) {
    return { draft: data.draft, results: data.results };
  }
  if (!r.ok) throw new Error(data.error || `${r.status} ${r.statusText}`);
  if (!data.draft) throw new Error("publish response missing draft");
  return { draft: data.draft, results: data.results ?? [] };
}

export interface BetaPostVersionSummary {
  version: number;
  generatedAt: number;
  /** English hook of that version — for the history preview. */
  quote?: string;
}

export async function listBetaPostVersions(id: string): Promise<BetaPostVersionSummary[]> {
  const r = await apiFetch(`/api/post-drafts-beta/${encodeURIComponent(id)}/versions`);
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  const data = (await r.json()) as { versions: BetaPostVersionSummary[] };
  return data.versions;
}

/** Cover-slide thumbnail URL for an archived version (history preview). */
export function betaPostVersionCoverUrl(id: string, version: number): string {
  return `/api/post-drafts-beta/${encodeURIComponent(id)}/versions/${version}/cover`;
}

/** Roll back: make archived `version` the live draft. Append-only — the current
 *  state is archived first, so it's reversible (restore the version that then
 *  appears at the top of the history to roll forward). */
export async function restoreBetaPostVersion(id: string, version: number): Promise<BetaPostDraft> {
  const r = await apiFetch(`/api/post-drafts-beta/${encodeURIComponent(id)}/restore`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ version }),
  });
  const data = (await r.json()) as { draft?: BetaPostDraft; error?: string };
  if (!r.ok || !data.draft) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data.draft;
}

export type BetaSlot = "background" | "01-cover-ar" | "02-cover-en" | "03-body-ar" | "04-body-en";

export async function listBetaPostDrafts(status?: BetaPostDraftStatus): Promise<BetaPostDraft[]> {
  const u = new URL("/api/post-drafts-beta", window.location.origin);
  if (status) u.searchParams.set("status", status);
  const r = await apiFetch(u.pathname + u.search);
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  const data = (await r.json()) as { drafts: BetaPostDraft[] };
  return data.drafts;
}

export type BetaSeedMode = "faithful" | "reinvent";

export async function generateBetaPostDraft(
  theme?: string,
  opts: { seedMode?: BetaSeedMode } = {},
): Promise<BetaPostDraft> {
  const payload: { theme?: string; seedMode?: BetaSeedMode } = {};
  if (theme) payload.theme = theme;
  if (opts.seedMode) payload.seedMode = opts.seedMode;
  const r = await apiFetch("/api/post-drafts-beta/generate", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = (await r.json()) as { draft?: BetaPostDraft; error?: string };
  if (!r.ok || !data.draft) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data.draft;
}

/** Subset of media types Claude vision accepts. */
export type ExtractableImageMediaType = "image/png" | "image/jpeg" | "image/webp" | "image/gif";

/**
 * OCR a quote screenshot via Claude vision so the operator can drop an
 * image into the seed field instead of round-tripping through ChatGPT.
 * Returns the literal text only — translation / felt-center distillation
 * still happen later in the post pipeline.
 */
export async function extractTextFromImage(file: File): Promise<string> {
  const accepted: ExtractableImageMediaType[] = ["image/png", "image/jpeg", "image/webp", "image/gif"];
  const mediaType = (accepted as string[]).includes(file.type)
    ? (file.type as ExtractableImageMediaType)
    : "image/png";
  const base64 = await fileToBase64(file);
  const r = await apiFetch("/api/post-drafts-beta/extract-text", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ image: base64, mediaType }),
  });
  const data = (await r.json().catch(() => ({}))) as { text?: string; error?: string };
  if (!r.ok) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return (data.text ?? "").trim();
}

/**
 * Read a File into a raw base64 string (no `data:...;base64,` prefix).
 * Resolves on FileReader's `load`, rejects on `error`.
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
    reader.readAsDataURL(file);
  });
}

/**
 * Regenerate a Beta post's text. With no `feedback`, it re-rolls a fresh
 * version (around the same theme/seed, snapshotting the prior version). With
 * `feedback`, the operator's note is injected into the generation prompt so
 * the re-roll addresses it. Re-renders slides in place if an image is attached.
 */
export async function regenerateBetaText(
  id: string,
  opts: { feedback?: string } = {},
): Promise<BetaPostDraft> {
  const feedback = opts.feedback?.trim();
  const r = await apiFetch(
    `/api/post-drafts-beta/${encodeURIComponent(id)}/regenerate-text`,
    feedback
      ? {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ feedback }),
        }
      : { method: "POST" },
  );
  const data = (await r.json()) as { draft?: BetaPostDraft; error?: string };
  if (!r.ok || !data.draft) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data.draft;
}

/**
 * Re-cut a Beta draft's HERO QUOTE shorter or longer (~15% per press),
 * leaving the reflections, image prompt and attached image untouched, then
 * re-render the slides. The prior version is archived server-side, so
 * Restore undoes it.
 *
 * A 409 is the expected "nothing changed" answer; its `error` text is already
 * operator-facing.
 */
export async function adjustBetaQuoteLength(
  id: string,
  direction: "shorter" | "longer",
): Promise<BetaPostDraft> {
  const r = await apiFetch(`/api/post-drafts-beta/${encodeURIComponent(id)}/quote-length`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ direction }),
  });
  const data = (await r.json()) as { draft?: BetaPostDraft; error?: string };
  if (!r.ok || !data.draft) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data.draft;
}

/** Regenerate the image promptHint only — keeps hook / Arabic /
 *  reflections intact. Used to retry after a GPT Image safety
 *  rejection or to get a different visual angle. */
export async function regenerateBetaPrompt(id: string): Promise<BetaPostDraft> {
  const r = await apiFetch(`/api/post-drafts-beta/${encodeURIComponent(id)}/regenerate-prompt`, {
    method: "POST",
  });
  const data = (await r.json()) as { draft?: BetaPostDraft; error?: string };
  if (!r.ok || !data.draft) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data.draft;
}

/**
 * Generate the draft's background image via OpenAI's gpt-image-2.
 * Server-side: writes background.png, re-renders the 4 slides, and
 * flips status to "ready-for-review" — same end-state as attach-image.
 * Defaults to high quality; pass a `quality` override only when the
 * operator picks a lower preset.
 */
export async function generateBetaImage(
  id: string,
  opts: {
    prompt?: string;
    quality?: "low" | "medium" | "high" | "auto";
    /** Up to 4 inspiration tile ids — gpt-image-2 receives them as
     *  visual style references via images.edit. Empty / omitted →
     *  text-only generation (same as before). */
    inspirationIds?: string[];
  } = {},
): Promise<BetaPostDraft> {
  const r = await apiFetch(`/api/post-drafts-beta/${encodeURIComponent(id)}/generate-image`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(opts),
  });
  const data = (await r.json()) as { draft?: BetaPostDraft; error?: string };
  if (!r.ok || !data.draft) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data.draft;
}

export async function attachBetaImage(id: string, source: { url?: string; filePath?: string }): Promise<BetaPostDraft> {
  const r = await apiFetch(`/api/post-drafts-beta/${encodeURIComponent(id)}/attach-image`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(source),
  });
  const data = (await r.json()) as { draft?: BetaPostDraft; error?: string };
  if (!r.ok || !data.draft) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data.draft;
}

export async function rerenderBetaSlides(id: string): Promise<BetaPostDraft> {
  const r = await apiFetch(`/api/post-drafts-beta/${encodeURIComponent(id)}/rerender`, {
    method: "POST",
  });
  const data = (await r.json()) as { draft?: BetaPostDraft; error?: string };
  if (!r.ok || !data.draft) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data.draft;
}

export async function discardBetaDraft(id: string): Promise<void> {
  const r = await apiFetch(`/api/post-drafts-beta/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

export function betaSlotUrl(id: string, slot: BetaSlot): string {
  return `/api/post-drafts-beta/${encodeURIComponent(id)}/${slot}`;
}

// ---------- inspiration library --------------------------------------

export type InspirationSource = "external" | "generated";

export interface InspirationEntry {
  id: string;
  ext: "png" | "jpg" | "jpeg" | "webp" | "gif";
  addedAt: number;
  note?: string;
  sourceUrl?: string;
  width?: number;
  height?: number;
  /** "external" (operator paste/URL — default) or "generated" (mirrored
   *  from a beta-post we shipped). Missing on legacy rows = external. */
  source?: InspirationSource;
  /** When source="generated": the beta-post id this tile mirrors. */
  betaPostId?: string;
  /** Version snapshot when this tile was archived. */
  betaPostVersion?: number;
}

/** Single place that defaults missing source to "external" so legacy
 *  rows render correctly on the All / External pills. */
export function entrySource(entry: Pick<InspirationEntry, "source">): InspirationSource {
  return entry.source === "generated" ? "generated" : "external";
}

export async function listInspiration(): Promise<InspirationEntry[]> {
  const r = await apiFetch("/api/inspiration");
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  const data = (await r.json()) as { entries: InspirationEntry[] };
  return data.entries;
}

/**
 * Add one inspiration tile. Either pass a base64 `dataUrl` (the
 * clipboard-paste path) or an http(s) `url` (the URL-paste path).
 * The server handles either, fetches the binary if needed, and
 * persists locally.
 */
export async function addInspiration(opts: {
  dataUrl?: string;
  url?: string;
  note?: string;
  sourceUrl?: string;
}): Promise<InspirationEntry> {
  const r = await apiFetch("/api/inspiration", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(opts),
  });
  const data = (await r.json()) as { entry?: InspirationEntry; error?: string };
  if (!r.ok || !data.entry) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data.entry;
}

export async function patchInspirationNote(id: string, note: string | null): Promise<InspirationEntry> {
  const r = await apiFetch(`/api/inspiration/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ note }),
  });
  const data = (await r.json()) as { entry?: InspirationEntry; error?: string };
  if (!r.ok || !data.entry) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data.entry;
}

export async function deleteInspiration(id: string): Promise<void> {
  const r = await apiFetch(`/api/inspiration/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

export function inspirationImageUrl(id: string): string {
  return `/api/inspiration/${encodeURIComponent(id)}/image`;
}

// ---- per-clip chat (Iterate with Claude) ----

export type ChatRole = "operator" | "agent" | "pipeline";

export interface ChatToolCall {
  tool: string;
  args: Record<string, unknown>;
  result: "succeeded" | "failed" | "pending";
  resultSummary?: string;
  error?: string;
}

export interface ChatMessage {
  id: string;
  clipName: string;
  role: ChatRole;
  content: string;
  createdAt: string;
  toolCalls?: ChatToolCall[];
}

export async function listClipChat(clipName: string): Promise<ChatMessage[]> {
  const r = await getJSON<{ messages: ChatMessage[] }>(
    `/api/clips/${encodeURIComponent(clipName)}/chat`,
  );
  return r.messages;
}

/** Send an operator message and wait for the agent reply. Synchronous;
 *  the server runs the agent loop before responding. Frontend should
 *  show a spinner over the input during the round-trip — tool-using
 *  agent turns can take 10-30s. */
export async function sendClipChat(
  clipName: string,
  content: string,
): Promise<{ operatorMessage: ChatMessage; agentMessage?: ChatMessage; error?: string }> {
  const r = await apiFetch(`/api/clips/${encodeURIComponent(clipName)}/chat`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ content }),
  });
  const data = (await r.json()) as {
    operatorMessage?: ChatMessage;
    agentMessage?: ChatMessage;
    error?: string;
  };
  if (!r.ok && !data.operatorMessage) {
    throw new Error(data.error || `${r.status} ${r.statusText}`);
  }
  return {
    operatorMessage: data.operatorMessage!,
    agentMessage: data.agentMessage,
    error: r.ok ? undefined : data.error,
  };
}

// ---- Studio iterate chat (Phase 3 — the chat loop) ----

/** One message in the clip's persistent Studio thread — mirror of the
 *  server's StudioChatMessage (src/lib/studioChat.ts). */
export interface StudioChatMessage {
  id: string;
  clipName: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
  /** User only: attached screenshots (data URLs) shown in scrollback. */
  images?: string[];
  /** Committable patch (storage syntax) when the turn proposed changes. */
  patch?: EditPatch;
  scope?: "clip" | "channel";
  /** Patch was returned appliable — the Studio commits it on receipt. */
  applied?: boolean;
  /** Channel-scope proposal awaiting Confirm / Just-this-clip. Resolved
   *  by a later message whose resolvesId points here. */
  pendingConfirmation?: boolean;
  resolvesId?: string;
  /** Assistant failure bubble (no patch). */
  error?: boolean;
  /** Which conversation the row belongs to. Absent ⇒ "default" — the
   *  bucket every row written before threads existed reads as. */
  threadId?: string;
}

/** One conversation in a clip's chat log (GET /iterate-threads). */
export interface ChatThreadInfo {
  id: string;
  /** First user message, trimmed — "New chat" until one exists. */
  title: string;
  updatedAt: string;
  count: number;
}

export interface IterateRequest {
  message: string;
  editState: ClipEditState;
  /** Which conversation this turn belongs to (client-generated id,
   *  [A-Za-z0-9_-]{1,64}). Omitted ⇒ the server's "default" thread. */
  threadId?: string;
  /** Model to spend on for this turn; an id outside the catalog falls
   *  through to the deployment default rather than failing the turn. */
  model?: string;
  selection?: {
    el: "caption" | "logo" | "speaker" | "video" | null;
    cueIdx: number | null;
    /** Clicked caption word (Tweak Mode phase 2) — targets "make this gold". */
    word?: string;
    wordIdx?: number;
  };
  /** Attached screenshots as downscaled data URLs (≤3) — the agent SEES them. */
  images?: string[];
  /** Confirmation turn for a pending channel-scope proposal. */
  confirmMessageId?: string;
  confirmChannelScope?: boolean;
}

export interface IterateResponse {
  userMessage: StudioChatMessage;
  /** Absent only when the server failed before persisting a reply. */
  assistantMessage?: StudioChatMessage;
  error?: string;
}

/** The clip's conversations, most recently touched first. Empty on a clip
 *  nobody has chatted about yet. */
export async function listStudioChatThreads(clipName: string): Promise<ChatThreadInfo[]> {
  const r = await getJSON<{ threads: ChatThreadInfo[] }>(
    `/api/clips/${encodeURIComponent(clipName)}/iterate-threads`,
  );
  return r.threads;
}

/** One conversation's scrollback. No `threadId` ⇒ the server's "default"
 *  thread, which is where every pre-threads row lives. */
export async function getStudioChatThread(
  clipName: string,
  threadId?: string,
): Promise<StudioChatMessage[]> {
  const q = threadId ? `?thread=${encodeURIComponent(threadId)}` : "";
  const r = await getJSON<{ messages: StudioChatMessage[] }>(
    `/api/clips/${encodeURIComponent(clipName)}/iterate-thread${q}`,
  );
  return r.messages;
}

/** One iterate turn. Synchronous — the server runs the model round-trip
 *  (typically 3-10s) before responding. On a 502 with a persisted error
 *  bubble the response still carries both messages plus `error`. */
export async function iterateClip(
  clipName: string,
  body: IterateRequest,
): Promise<IterateResponse> {
  const r = await apiFetch(`/api/clips/${encodeURIComponent(clipName)}/iterate`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await r.json()) as IterateResponse & { error?: string };
  if (!r.ok && !data.userMessage) {
    throw new Error(data.error || `${r.status} ${r.statusText}`);
  }
  return { ...data, error: r.ok ? undefined : data.error };
}

// ---- Studio Phase 4 — persistence, approve, test render, promotion ----

async function postJSON<T>(path: string, body: unknown): Promise<T> {
  const r = await apiFetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data;
}

/** postJSON's PATCH twin — same error contract (`{ error }` body → thrown message). */
async function patchJSON<T>(path: string, body: unknown): Promise<T> {
  const r = await apiFetch(path, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data;
}

// ---- Studio styling v2 — custom presets + channel font upload ----

export interface CustomStylePreset {
  id: string;
  label: string;
  patch: EditPatch;
  createdAt: number;
}

export async function listStylePresets(): Promise<CustomStylePreset[]> {
  const r = await getJSON<{ presets: CustomStylePreset[] }>("/api/style-presets");
  return r.presets;
}

export async function saveStylePreset(
  label: string,
  patch: EditPatch,
): Promise<CustomStylePreset> {
  const r = await postJSON<{ preset: CustomStylePreset }>("/api/style-presets", { label, patch });
  return r.preset;
}

export async function deleteStylePreset(id: string): Promise<void> {
  const r = await apiFetch(`/api/style-presets/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

/** What POST /api/channels/:slug/fonts did with a family (spec 2026-09-16
 *  round 2 §2.5): `uploaded` (new bytes registered), `adopted` (another
 *  brand's faces copied onto this channel), `existing` (already on this
 *  channel) or `library` (a founding/library face — pickable as is). */
export type FontAddStatus = "uploaded" | "adopted" | "existing" | "library";

/** Upload a .ttf/.otf to a channel's registered faces (Studio font picker).
 *  `status` is absent on older servers. */
export async function uploadChannelFont(
  slug: string,
  family: string,
  dataUrl: string,
): Promise<{ family: string; status?: FontAddStatus }> {
  return postJSON(`/api/channels/${encodeURIComponent(slug)}/fonts`, { family, dataUrl });
}

/** Adopt a family that already lives on another brand onto this channel — no
 *  bytes travel. A family nobody has is a 400 — upload it. */
export async function adoptChannelFont(
  slug: string,
  family: string,
): Promise<{ family: string; status: FontAddStatus }> {
  return postJSON(`/api/channels/${encodeURIComponent(slug)}/fonts`, { family });
}

/** The server-persisted edit journal (work/<clip>/edit-state.json). `exists`
 *  distinguishes "never saved" from "saved empty". */
export async function getEditState(
  clipName: string,
): Promise<{ exists: boolean; editState: ClipEditState }> {
  return getJSON(`/api/clips/${encodeURIComponent(clipName)}/edit-state`);
}

export async function putEditState(clipName: string, editState: ClipEditState): Promise<void> {
  const r = await apiFetch(`/api/clips/${encodeURIComponent(clipName)}/edit-state`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ editState }),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `${r.status} ${r.statusText}`);
  }
}

export interface ApproveRenderResult {
  ok: boolean;
  /** How many cue rewrites were baked into arabic.srt. */
  bakedCues: number;
  designSteps: number;
  /** The journal after baking ({bakedAt} markers) — replace the local one. */
  editState: ClipEditState;
}

/** Approve & render: persist the journal, bake cue edits into the SRT and
 *  re-render with the design applied. Async — poll getJob(clipName) for
 *  progress like any other re-render. */
export async function approveRender(
  clipName: string,
  editState: ClipEditState,
): Promise<ApproveRenderResult> {
  return postJSON(`/api/clips/${encodeURIComponent(clipName)}/approve-render`, { editState });
}

export interface TestRenderWindow {
  cueIdx: number;
  startFrame: number;
  endFrame: number;
  startSec: number;
  endSec: number;
}

export interface TestRenderStatus {
  id: string;
  status: "running" | "done" | "failed";
  startedAt: number;
  window?: TestRenderWindow;
  error?: string;
}

/** Kick off a ~6s REAL-renderer slice of the current edit state. */
export async function startTestRender(
  clipName: string,
  body: { aroundCue?: number | null; editState: ClipEditState },
): Promise<{ id: string }> {
  return postJSON(`/api/clips/${encodeURIComponent(clipName)}/test-render`, body);
}

export async function getTestRenderStatus(clipName: string, id: string): Promise<TestRenderStatus> {
  return getJSON(`/api/clips/${encodeURIComponent(clipName)}/test-render/${encodeURIComponent(id)}`);
}

export function testRenderFileUrl(clipName: string, id: string): string {
  return `/api/clips/${encodeURIComponent(clipName)}/test-render/${encodeURIComponent(id)}/file`;
}

export interface PromoteDesignResult {
  ok: boolean;
  channel: string;
  promotedFields: string[];
  skippedFields: string[];
  /** The journal with {scope:"channel", promotedAt} markers — replace local. */
  editState: ClipEditState;
}

/** Save the clip's accumulated design (layout+brand) to its channel so every
 *  future video on that channel renders with it. */
export async function promoteDesign(
  clipName: string,
  editState: ClipEditState,
): Promise<PromoteDesignResult> {
  return postJSON(`/api/clips/${encodeURIComponent(clipName)}/promote-design`, { editState });
}

// ---------------------------------------------------------------- autopilot

export interface AutopilotChannelStatus {
  slug: string;
  displayName: string;
  enabled: boolean;
  perDay: number;
  /** Channel has both autoScan + videos features — the cron acts only then. */
  eligible: boolean;
  today: { rendered: number; scans: number; failures: number; standingDown: boolean };
}

export interface AutopilotStatus {
  /** True when QL_AUTOPILOT=off on the server — toggles are moot. */
  killSwitchOn: boolean;
  schedule: string;
  timezone: string;
  maxPerDay: number;
  channels: AutopilotChannelStatus[];
  log: Array<{ at: number; channelSlug: string; kind: "scan" | "render" | "skip" | "error"; detail: string }>;
}

export async function getAutopilot(): Promise<AutopilotStatus> {
  const r = await apiFetch("/api/autopilot");
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return (await r.json()) as AutopilotStatus;
}

/** Partial per-channel patch — server re-coerces (perDay clamps to 1–3). */
export async function updateAutopilot(
  channels: Record<string, { enabled?: boolean; perDay?: number }>,
): Promise<AutopilotStatus> {
  const r = await apiFetch("/api/autopilot", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ channels }),
  });
  const data = (await r.json().catch(() => ({}))) as AutopilotStatus & { error?: string };
  if (!r.ok) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data;
}

// ------------------------------------------------- AI provider, credits, usage

/** One model this deployment offers — mirror of src/lib/modelCatalog.ts's
 *  ModelInfo. Prices are USD per MILLION tokens; `null` = metered but not
 *  priced here (an env-configured model), which the UI renders as a blank
 *  rather than a guess. */
export interface AiModelInfo {
  id: string;
  label: string;
  tier: "flagship" | "balanced" | "fast";
  inputUsdPerM: number | null;
  outputUsdPerM: number | null;
  cacheReadUsdPerM: number | null;
  cacheWriteUsdPerM: number | null;
  /** False ⇒ the model rejects a FORCED tool call, so it cannot run the
   *  design agent (every turn forces one). The PUT rejects it too. */
  forcedToolUse: boolean;
}

export interface AiTopup {
  at: string;
  usd: number;
  note?: string;
}

export interface AiCredits {
  /** May be NEGATIVE by design — we never block a render for money. */
  balanceUsd: number;
  /** Derived from the ledger (platform-key rows only). */
  spentUsd: number;
  topups: AiTopup[];
}

/** GET /api/ai/settings — and the body every mutation answers with, so a
 *  write always leaves the client holding fresh truth. Key material is
 *  structurally absent: only the last 4 characters ever cross the wire. */
export interface AiSettingsInfo {
  /** False when QL_AI_SETTINGS=off — the env key is the only payer. */
  enabled: boolean;
  provider: {
    mode: "platform" | "own";
    ownKeyLast4?: string;
    /** ISO time the key last verified against the Anthropic API. */
    ownKeyOkAt?: string;
    /** False when a key is stored but this box had no session secret to
     *  encrypt it with — the card says so rather than staying quiet. */
    keyEncrypted?: boolean;
  };
  /** Empty when no override is set — `defaultModel` is then in force. */
  designAgent: { model?: string };
  defaultModel: string;
  models: AiModelInfo[];
  credits: AiCredits;
}

export async function getAiSettings(): Promise<AiSettingsInfo> {
  return getJSON<AiSettingsInfo>("/api/ai/settings");
}

/** Switch platform ⇄ own key, paste a key, or pick the design-agent model.
 *  A rejected key or model answers 422 with a human message — thrown here so
 *  the card can show it inline. `designAgent.model: null` clears the override
 *  back to the deployment default. */
export async function putAiSettings(patch: {
  provider?: { mode?: "platform" | "own"; ownKey?: string };
  designAgent?: { model?: string | null };
}): Promise<AiSettingsInfo> {
  const r = await apiFetch("/api/ai/settings", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  const data = (await r.json().catch(() => ({}))) as AiSettingsInfo & { error?: string };
  if (!r.ok) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data;
}

/** Forget the stored own key (and fall back to the platform key). */
export async function deleteOwnKey(): Promise<AiSettingsInfo> {
  const r = await apiFetch("/api/ai/settings/own-key", { method: "DELETE" });
  const data = (await r.json().catch(() => ({}))) as AiSettingsInfo & { error?: string };
  if (!r.ok) throw new Error(data.error || `${r.status} ${r.statusText}`);
  return data;
}

/** Operator top-up — a payment provider is a later ticket. */
export async function topUpCredits(usd: number, note?: string): Promise<AiCredits> {
  return postJSON<AiCredits>("/api/ai/credits/topup", { usd, ...(note ? { note } : {}) });
}

/** One metered call — mirror of src/lib/usageLedger.ts's UsageRow. */
export interface UsageRow {
  at: string;
  kind: "anthropic" | "whisper";
  model: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  /** Whisper only. */
  minutes?: number;
  /** null = metered but not priced (unknown / env-only model). */
  usd: number | null;
  provider: "platform" | "own";
  scope: string;
  clip?: string;
  channel?: string;
  threadId?: string;
  note?: string;
}

export interface UsageSummary {
  count: number;
  totalUsd: number;
  byScope: Record<string, { usd: number; count: number }>;
  byModel: Record<string, { usd: number; inputTokens: number; outputTokens: number; count: number }>;
  byDay: Array<{ day: string; usd: number; count: number }>;
}

export interface AiUsageReport {
  days: number;
  /** Newest first, capped server-side — the summary still covers every row. */
  rows: UsageRow[];
  summary: UsageSummary;
  credits: AiCredits;
  provider: "platform" | "own";
}

export async function getAiUsage(days = 30): Promise<AiUsageReport> {
  return getJSON<AiUsageReport>(`/api/ai/usage?days=${encodeURIComponent(String(days))}`);
}

// ---------- brand study (Brand Setup → "Read my brand") ----------

/**
 * Mirrors of `src/lib/brandStudy/types.ts`. Written out rather than imported
 * so the web bundle never pulls the server tree's `channels.ts` in for a
 * type — the same rule the channel mirrors above follow.
 */
export type StudySource =
  | { kind: "url"; url: string }
  | { kind: "upload"; uploadId: string; filename: string };

export type StudyStepId = "fetched" | "frames" | "measured" | "understood";

export interface StudyStep {
  id: StudyStepId;
  status: "pending" | "running" | "done" | "failed" | "skipped";
  detail?: string;
}

/** Why one source could not be read — rendered as a SENTENCE, never a trace. */
export interface StudyFailure {
  source: StudySource;
  kind: "unreachable" | "login-wall" | "unsupported" | "too-large";
  message: string;
}

/** One accept/reset row in Brand Setup. `field` is the dotted path into the
 *  patch (`brand.captionAccent`) or into `extras` (`fontRoles.caption`). */
export interface StudyChip {
  field: string;
  label: string;
  value: unknown;
  confidence: "high" | "medium" | "low";
  source: "measured" | "model" | "default" | "adjusted";
}

/** Study output that is NOT an `EditPatch` field (mirror of `StudyExtras`). */
export interface StudyExtras {
  cueShape?: { maxWords?: number; maxChars?: number; maxDurSec?: number; accentWords?: 0 | 1 | 2 };
  language?: string;
  brandName?: string;
  fontRoles?: { caption?: string; punch?: string; speaker?: string; speakerName?: string };
  letterSpacingPx?: number;
  speakerNameColor?: string;
}

/** `data/brand-studies/<id>/study.json` — what the reading state polls. */
export interface BrandStudyRecord {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: "queued" | "running" | "done" | "failed";
  steps: StudyStep[];
  sources: StudySource[];
  failures: StudyFailure[];
  patch?: EditPatch;
  extras?: StudyExtras;
  chips?: StudyChip[];
  error?: string;
  /** The study itself. Only the one field the wizard needs is declared: the
   *  Vision step fails OPEN (colours and geometry still land), so this is how
   *  the UI knows to say the typography read did not happen. */
  study?: { visionError?: string };
}

export interface BrandStudyUpload {
  uploadId: string;
  filename: string;
  sizeBytes: number;
  kind: "video" | "image";
}

/**
 * Stream one reel / post image to `POST /api/brand-study/upload`. XHR for the
 * same reason `uploadVideo` uses it: only XHR reports request-body progress.
 */
export function uploadBrandStudyFile(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<BrandStudyUpload> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.withCredentials = true;
    xhr.open("POST", `/api/brand-study/upload?filename=${encodeURIComponent(file.name)}`);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => {
      if (xhr.status === 401) {
        emitAuthExpired();
        reject(new Error("auth required"));
        return;
      }
      let body: Partial<BrandStudyUpload> & { error?: string } = {};
      try {
        body = JSON.parse(xhr.responseText) as Partial<BrandStudyUpload> & { error?: string };
      } catch {
        /* non-JSON body; fall through to the status-based error below */
      }
      if (xhr.status >= 200 && xhr.status < 300 && body.uploadId) {
        resolve({
          uploadId: body.uploadId,
          filename: body.filename ?? file.name,
          sizeBytes: body.sizeBytes ?? file.size,
          kind: body.kind ?? "video",
        });
      } else {
        reject(new Error(body.error || `upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error("network error during upload"));
    xhr.send(file);
  });
}

/** Thrown when a study is already running (HTTP 409) so the wizard can say so
 *  in its own words instead of leaking the server sentence twice. */
export class BrandStudyBusyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BrandStudyBusyError";
  }
}

/** Kick off a study. 202 → `{ id }`; 409 → `BrandStudyBusyError`. */
export async function startBrandStudy(sources: StudySource[]): Promise<{ id: string }> {
  const r = await apiFetch("/api/brand-study/start", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sources }),
  });
  const body = (await r.json().catch(() => ({}))) as { id?: string; error?: string };
  if (r.status === 409) throw new BrandStudyBusyError(body.error || "a brand is already being read");
  if (!r.ok || !body.id) throw new Error(body.error || `${r.status} ${r.statusText}`);
  return { id: body.id };
}

export async function getBrandStudy(id: string): Promise<BrandStudyRecord> {
  return getJSON<BrandStudyRecord>(`/api/brand-study/${encodeURIComponent(id)}`);
}
